
/* ─────────────────────────────────────────────
   PROFILE STATE 
───────────────────────────────────────────── */
Object.assign(state, {
  // Current user (loaded from /users/me)
  profileUser:              state.profileUser              ?? null,
  profileLoading:           state.profileLoading           ?? false,
  deleteAddressConfirmId:   state.deleteAddressConfirmId   ?? null,
  deletePhoneConfirmId:     state.deletePhoneConfirmId     ?? null,

  // Addresses
  addresses:                state.addresses                ?? [],
  addressesLoading:         state.addresses                ?? false,
  addressesError:           state.addressesError           ?? null,

  // Phone numbers
  phones:                   state.phones                   ?? [],
  phonesLoading:            state.phonesLoading            ?? false,
  phonesError:              state.phonesError              ?? null,

  // Support tickets
  tickets:                  state.tickets                  ?? [],
  ticketsLoading:           state.ticketsLoading           ?? false,
  ticketsError:             state.ticketsError             ?? null,

 newTicketMessage:      state.newTicketMessage      ?? '',
  newTicketSubmitting:   state.newTicketSubmitting    ?? false,
  newTicketError:        state.newTicketError         ?? '',
  newTicketSubmitSuccess: state.newTicketSubmitSuccess ?? false,

  // Account deletion
  deletionPending:          state.deletionPending          ?? false,
  deletionLoading:          state.deletionLoading          ?? false,
  deletionOtpStep:          state.deletionOtpStep          ?? false,   // show OTP input
  deletionOtpCode:          state.deletionOtpCode          ?? '',
  deletionOtpVerifying:     state.deletionOtpVerifying     ?? false,

  // Change password
  changePasswordStep:       state.changePasswordStep       ?? false,
  cpOldPw:                  state.cpOldPw                  ?? '',
  cpNewPw:                  state.cpNewPw                  ?? '',
  cpConfirmPw:              state.cpConfirmPw              ?? '',
  cpLoading:                state.cpLoading                ?? false,
  cpError:                  state.cpError                  ?? '',
  cpShowOld:                state.cpShowOld                ?? false,
  cpShowNew:                state.cpShowNew                ?? false,
  cpShowConfirm:            state.cpShowConfirm            ?? false,

  // Sub-screens within profile
  profileSubscreen:         state.profileSubscreen         ?? null,
  // possible values: null | 'addresses' | 'phones' | 'tickets'
  //                | 'editAddress' | 'addAddress'
  //                | 'editPhone'   | 'addPhone'
  //                | 'ticketDetail'| 'changePassword'
  //                | 'deleteAccount'

  // Address form
  addrForm: state.addrForm ?? {
    id: null, purpose: 'shipping',
    line1: '', line2: '', city: '', stateCode: '', zip: '',
    errors: {}, saving: false,
  },

  // Shop details
  shopDetails:              state.shopDetails              ?? null,
  shopDetailsLoading:       state.shopDetailsLoading       ?? false,
  shopDetailsError:         state.shopDetailsError         ?? null,
  

  // Phone form
  phoneForm: state.phoneForm ?? {
    id: null, phone: '', errors: {}, saving: false,
  },

  // Ticket detail
  currentTicket:            state.currentTicket            ?? null,
  ticketMessages:           state.ticketMessages           ?? [],
  ticketMessagesLoading:    state.ticketMessagesLoading    ?? false,
  ticketReplyText:          state.ticketReplyText          ?? '',
  ticketReplySending:       state.ticketReplySending       ?? false,

  // Logout confirm dialog
  logoutDialogOpen:         state.logoutDialogOpen         ?? false,
});

/* ─────────────────────────────────────────────
   API CALLS
───────────────────────────────────────────── */

async function loadProfileUser() {
  state.profileLoading = true;
  try {
    const data = await apiFetch(`${BASE_URL}/users`);
    const user = Array.isArray(data) ? data[0] : data; 
    
    console.log('👤 Parsed User:', user);
    console.log('🏪 Shop ID:', user?.shopId);
    
    state.profileUser = user;
    
    state.customer = {
      name:  user?.legalName || user?.name || user?.email || 'Customer',
      email: user?.email || '',
      role:  user?.role  || 'customer',
      phone: user?.phoneNumber || '',
      shopName: '',  
      shopId:   user?.shopId   || '', 
    };
  } catch (e) {
    console.warn('Could not load profile user:', e.message);
  } finally {
    state.profileLoading = false;
    patchContent();
  }
}

async function loadAddresses() {
  state.addressesLoading = true;
  state.addressesError   = null;
  patchContent();
  try {
    const data = await apiFetch(`${BASE_URL}/users/me/saved-addresses`);
    state.addresses = Array.isArray(data) ? data : (data.addresses || data.data || []);
  } catch (e) {
    state.addressesError = e.message || 'Failed to load addresses';
    state.addresses = [];
  } finally {
    state.addressesLoading = false;
    patchContent();
  }
}

async function loadPhones() {
  state.phonesLoading = true;
  state.phonesError   = null;
  patchContent();
  try {
    const data = await apiFetch(`${BASE_URL}/users/me/saved-phone-numbers`);
    state.phones = Array.isArray(data) ? data : (data.phones || data.data || []);
  } catch (e) {
    state.phonesError = e.message || 'Failed to load phone numbers';
    state.phones = [];
  } finally {
    state.phonesLoading = false;
    patchContent();
  }
}

async function loadTickets() {
  state.ticketsLoading = true;
  state.ticketsError   = null;
  patchContent();
  try {
    const data = await apiFetch(`${BASE_URL}/support/tickets/my`);
    state.tickets = Array.isArray(data) ? data : (data.tickets || data.data || []);
  } catch (e) {
    state.ticketsError = e.message || 'Failed to load support tickets';
    state.tickets = [];
  } finally {
    state.ticketsLoading = false;
    patchContent();
  }
}

async function loadTicketDetail(ticketId) {
  state.ticketMessagesLoading = true;
  patchContent();

  try {
    const ticket = await apiFetch(
      `${BASE_URL}/support/tickets/my/${ticketId}`
    );

    state.currentTicket = ticket;

    state.ticketMessages = Array.isArray(ticket.messages)
      ? ticket.messages
      : [];

    console.log('📬 Loaded messages:', state.ticketMessages);

  } catch (e) {
    console.error('Ticket detail error:', e);
    showToast('Could not load ticket details');
  } finally {
    state.ticketMessagesLoading = false;
    patchContent();
  }
}

async function submitNewTicket() {
  const liveInput = document.getElementById('new-ticket-textarea');
  if (liveInput) state.newTicketMessage = liveInput.value;
  const message = (state.newTicketMessage || '').trim();

  if (!message) {
    const errEl = document.getElementById('ticket-err');
    if (errEl) { errEl.textContent = 'Please enter your question'; errEl.style.display = 'block'; }
    else { state.newTicketError = 'Please enter your question'; patchContent(); }
    return;
  }
  if (message.length < 10) {
    const errEl = document.getElementById('ticket-err');
    if (errEl) { errEl.textContent = 'Please enter at least 10 characters'; errEl.style.display = 'block'; }
    else { state.newTicketError = 'Please enter at least 10 characters'; patchContent(); }
    return;
  }

  state.newTicketSubmitting = true;
  state.newTicketError = '';
  patchContent();

  try {
    await apiFetch(`${BASE_URL}/support/tickets`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
    state.newTicketMessage = '';
    state.newTicketError = '';
    state.newTicketSubmitSuccess = true;
    patchContent();
  } catch (e) {
    state.newTicketError = e.message || 'Something went wrong. Please try again.';
  } finally {
    state.newTicketSubmitting = false;
    patchContent();
  }
}

function renderNewTicketScreen() {
  if (state.newTicketSubmitSuccess) {
    return `
      <div style="max-width:600px;margin:0 auto">
        ${profileBackHeader('Support', "state.profileSubscreen='tickets';state.newTicketSubmitSuccess=false;patchContent()")}
        <div style="padding:48px 24px;display:flex;flex-direction:column;align-items:center;text-align:center">
          <div style="width:72px;height:72px;border-radius:50%;background:#e8f5e9;
                      display:flex;align-items:center;justify-content:center;margin-bottom:20px">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#169406" stroke-width="2.5">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
          </div>
          <p style="font-size:20px;font-weight:700;color:#1a2a2a;margin:0 0 12px">Request submitted</p>
          <p style="font-size:14px;color:#888;margin:0 0 32px;line-height:1.6;max-width:320px">
            Your request has been logged. We have sent a confirmation to your email.
            Our team will review it and respond as soon as possible.
          </p>
          <button onclick="state.profileSubscreen='tickets';state.newTicketSubmitSuccess=false;loadTickets();patchContent()"
                  style="padding:14px 40px;background:#FFCC02;color:#1D4064;border:none;
                         border-radius:12px;font-size:16px;font-weight:700;cursor:pointer;
                         font-family:DM Sans,sans-serif">
            OK
          </button>
        </div>
      </div>`;
  }

  return `
    <div style="max-width:600px;margin:0 auto">
      ${profileBackHeader('Support', "state.profileSubscreen='tickets';patchContent()")}
      <div style="padding:32px 24px;display:flex;flex-direction:column;align-items:center">

        <p style="font-size:20px;font-weight:700;color:#1a2a2a;margin:0 0 24px;text-align:center">
          Raise Support Request
        </p>

        <div style="width:100%;margin-bottom:8px">
          <textarea
  id="new-ticket-textarea"
  placeholder="How can we help you?"
  maxlength="2000"
  oninput="state.newTicketMessage=this.value;"
            style="width:100%;min-height:160px;padding:16px;
                   border:1.5px solid ${state.newTicketError ? '#e24b4a' : '#dde3e8'};
                   border-radius:12px;font-size:16px;color:#1a2a2a;resize:none;outline:none;
                   box-sizing:border-box;font-family:DM Sans,sans-serif;line-height:1.6"
            onfocus="this.style.borderColor='${state.newTicketError ? '#e24b4a' : '#4b9e95'}'"
            onblur="this.style.borderColor='${state.newTicketError ? '#e24b4a' : '#dde3e8'}'"
          >${escHtml(state.newTicketMessage)}</textarea>

         <p id="ticket-err" style="font-size:13px;color:#e24b4a;margin:4px 0 0 4px;
   display:${state.newTicketError ? 'block' : 'none'}">
  ${escHtml(state.newTicketError)}
</p>
        </div>

        <div style="width:100%;margin-top:8px">
          ${profileSaveBtn('Submit', 'submitNewTicket()', state.newTicketSubmitting)}
        </div>

      </div>
    </div>
  `;
}

async function loadDeletionStatus() {
  state.deletionLoading = true;
  try {
    const data = await apiFetch(`${BASE_URL}/account-requests/my-pending-deletion`);
    state.deletionPending = !!(data.pending || data.hasPending || data.exists);
  } catch {
    state.deletionPending = false;
  } finally {
    state.deletionLoading = false;
    patchContent();
  }
}

/* ─────────────────────────────────────────────
   ADDRESS ACTIONS
───────────────────────────────────────────── */
function openAddAddress(purpose) {
  state.addrForm = { id: null, purpose: purpose || 'shipping', line1: '', line2: '', city: '', stateCode: '', zip: '', errors: {}, saving: false };
  state.profileSubscreen = 'addAddress';
  patchContent();
}

function openEditAddress(addr) {
  state.addrForm = {
    id: addr._id,
    purpose: addr.addressPurpose || 'shipping',
    line1: addr.addressLine1 || '',
    line2: addr.addressLine2 || '',
    city:  addr.city  || '',
    stateCode: addr.state || addr.stateCode || '',
    zip:   addr.postalCode || addr.zip || '',
    errors: {}, saving: false,
  };
  state.profileSubscreen = 'editAddress';
  patchContent();
}


async function deleteAddress(id) {
  state.deleteAddressConfirmId = id;
  patchContent();
}

async function confirmDeleteAddress() {
  const id = state.deleteAddressConfirmId;
  if (!id) return;
  state.deleteAddressConfirmId = null;
  patchContent();
  try {
    await apiFetch(
      `${BASE_URL}/users/me/saved-addresses/${encodeURIComponent(id)}`,
      { method: 'DELETE' }
    );
    state.addresses = state.addresses.filter(a => a._id !== id);
    showToast('Address deleted');
    patchContent();
  } catch { showToast('Could not delete address'); }
}

function cancelDeleteAddress() {
  state.deleteAddressConfirmId = null;
  patchContent();
}

function validateAddrForm() {
  const f = state.addrForm;
  const e = {};
  if (!f.line1.trim())     e.line1     = 'Required';
  if (!f.city.trim())      e.city      = 'Required';
  if (!f.stateCode.trim()) e.stateCode = 'Required';
  if (!f.zip.trim())       e.zip       = 'Required';
  else if (!/^\d{5}(-\d{4})?$/.test(f.zip.trim())) e.zip = 'Enter a valid US ZIP code';
  state.addrForm.errors = e;
  return Object.keys(e).length === 0;
}

async function saveAddress() {
  if (!validateAddrForm()) { patchContent(); return; }
  state.addrForm.saving = true;
  patchContent();
  const f = state.addrForm;
console.log('saveAddress called — f.id:', f.id, '| full form:', JSON.stringify(f));
  const body = {
    addressLine1:   f.line1.trim(),
    addressLine2:   f.line2.trim() || undefined,
    city:           f.city.trim(),
    state:          f.stateCode.trim().toUpperCase(),
    postalCode:     f.zip.trim(),
    country:        'US',
    addressPurpose: f.purpose,
  };
 try {
    if (f.id) {
      await apiFetch(
        `${BASE_URL}/users/me/saved-addresses/${encodeURIComponent(f.id)}`,
        { method: 'PATCH', body: JSON.stringify(body) }
      );
      state.addresses = state.addresses.map(a =>
        a._id === f.id
          ? {
              ...a,
              addressLine1:   body.addressLine1,
              addressLine2:   body.addressLine2,
              city:           body.city,
              state:          body.state,
              postalCode:     body.postalCode,
              country:        body.country,
              addressPurpose: body.addressPurpose,
            }
          : a
      );
      showToast('Address updated');
      state.profileSubscreen = 'addresses';
      loadAddresses(); // refresh from server in background
    } else {
      const created = await apiFetch(
        `${BASE_URL}/users/me/saved-addresses`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      // Reload from server so we get the real _id back
      await loadAddresses();
      showToast('Address saved');
      state.profileSubscreen = 'addresses';
    }
  } catch (e) {
    showToast(e.message || 'Could not save address');
  }finally {
    state.addrForm.saving = false;
    patchContent();
  }
}

/* ─────────────────────────────────────────────
   PHONE ACTIONS
───────────────────────────────────────────── */
function openAddPhone() {
  state.phoneForm = { id: null, phone: '', errors: {}, saving: false };
  state.profileSubscreen = 'addPhone';
  patchContent();
}

function openEditPhone(p) {
  state.phoneForm = { _id: p._id, phone: p.phone || '', errors: {}, saving: false };
  state.profileSubscreen = 'editPhone';
  patchContent();
}

async function deletePhone(phoneId) {
  if (!phoneId) { showToast('Invalid phone number'); return; }
  state.deletePhoneConfirmId = phoneId;
  patchContent();
}

async function confirmDeletePhone() {
  const phoneId = state.deletePhoneConfirmId;
  if (!phoneId) return;
  state.deletePhoneConfirmId = null;
  patchContent();
  try {
    await apiFetch(
      `${BASE_URL}/users/me/saved-phone-numbers/${encodeURIComponent(phoneId)}`,
      { method: 'DELETE' }
    );
    state.phones = state.phones.filter(p => p._id !== phoneId);
    showToast('Phone number deleted');
    patchContent();
  } catch (e) {
    console.error(e);
    showToast('Could not delete phone number');
  }
}

function cancelDeletePhone() {
  state.deletePhoneConfirmId = null;
  patchContent();
}

function formatPhoneDisplay(digits) {
  const d = (digits || '').replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
}

function validatePhoneForm() {
  const d = (state.phoneForm.phone || '').replace(/\D/g, '');
  const e = {};
  if (!d) e.phone = 'Required';
  else if (d.length !== 10) e.phone = 'Enter a valid 10-digit US phone number';
  state.phoneForm.errors = e;
  return Object.keys(e).length === 0;
}

async function savePhone() {

  if (!validatePhoneForm()) {
    patchContent();
    return;
  }

  state.phoneForm.saving = true;

  patchContent();

  const digits = (state.phoneForm.phone || '').replace(/\D/g, '');

  const body = {
    phone: digits,
    phonePurpose: 'shipping'
  };

  try {

    // UPDATE
    if (state.phoneForm._id) {

      const updated = await apiFetch(
        `${BASE_URL}/users/me/saved-phone-numbers/${encodeURIComponent(state.phoneForm._id)}`,
        {
          method: 'PATCH',
          body: JSON.stringify(body)
        }
      );

      state.phones = state.phones.map(p =>
        p._id === state.phoneForm._id
          ? { ...p, ...body }
          : p
      );

      showToast('Phone number updated');

    } else {

      // CREATE
      const created = await apiFetch(
        `${BASE_URL}/users/me/saved-phone-numbers`,
        {
          method: 'POST',
          body: JSON.stringify(body)
        }
      );

      state.phones.push(created);

      showToast('Phone number saved');
    }

    state.profileSubscreen = 'phones';

  } catch (e) {

    console.error(e);

    showToast(e.message || 'Could not save phone number');

  } finally {

    state.phoneForm.saving = false;

    patchContent();
  }
}

/* ─────────────────────────────────────────────
   CHANGE PASSWORD
───────────────────────────────────────────── */
async function submitChangePassword() {
  state.cpError = '';
  if (!state.cpOldPw)     { state.cpError = 'Current password is required'; patchContent(); return; }
  if (!state.cpNewPw)     { state.cpError = 'New password is required'; patchContent(); return; }
  if (state.cpNewPw.length < 8) { state.cpError = 'New password must be at least 8 characters'; patchContent(); return; }
  if (state.cpNewPw !== state.cpConfirmPw) { state.cpError = 'Passwords do not match'; patchContent(); return; }
  state.cpLoading = true;
  patchContent();
  
  try {
    await apiFetch(`${BASE_URL}/auth/change-password`, {
      method: 'POST',
      body: JSON.stringify({ currentPassword: state.cpOldPw, newPassword: state.cpNewPw }),
    });
    showToast('Password changed successfully');
    state.cpOldPw = ''; state.cpNewPw = ''; state.cpConfirmPw = '';
    state.profileSubscreen = null;
  } catch (e) {
    state.cpError = e.message || 'Could not change password. Please check your current password.';
  } finally {
    state.cpLoading = false;
    patchContent();
  }
}

/* ─────────────────────────────────────────────
   ACCOUNT DELETION
───────────────────────────────────────────── */
async function startAccountDeletion() {
  if (!confirm('We will email a 6-digit code to verify it is you. After you enter the code, your deletion request is sent to an administrator for approval.\n\nSend code?')) return;
  state.deletionLoading = true;
  patchContent();
  try {
    await apiFetch(`${BASE_URL}/users/me/request-account-deletion-otp`, { method: 'POST' });
    state.deletionOtpStep = true;
    state.deletionOtpCode = '';
    state.profileSubscreen = 'deleteAccount';
  } catch (e) {
    showToast(e.message || 'Could not send verification code');
  } finally {
    state.deletionLoading = false;
    patchContent();
  }
}

async function verifyDeletionOtp() {
  if ((state.deletionOtpCode || '').length !== 6) { showToast('Enter the 6-digit code'); return; }
  state.deletionOtpVerifying = true;
  patchContent();
  try {
    await apiFetch(`${BASE_URL}/users/me/confirm-account-deletion`, {
      method: 'POST',
      body: JSON.stringify({ code: state.deletionOtpCode }),
    });
    state.deletionPending  = true;
    state.deletionOtpStep  = false;
    state.profileSubscreen = null;
    showToast('Deletion request submitted. We will email you when reviewed.');
  } catch (e) {
    showToast(e.message || 'Invalid code or request failed');
  } finally {
    state.deletionOtpVerifying = false;
    patchContent();
  }
}

/* ─────────────────────────────────────────────
   TICKET REPLY - FIXED (No Redirect)
───────────────────────────────────────────── */
async function sendTicketReply(ticketId) {
  const liveInput = document.getElementById('ticket-reply-input');
  if (liveInput) state.ticketReplyText = liveInput.value;
  const text = (state.ticketReplyText || '').trim();
  
  if (!text) { 
    showToast('Please type a message'); 
    return; 
  }
  
  const token = localStorage.getItem('token');
  console.log('🔐 Send Reply Debug:', {
    ticketId,
    tokenPresent: !!token,
    tokenStart: token ? token.substring(0, 30) + '...' : null
  });
  
  state.ticketReplySending = true;
  patchContent();
  
  try {
    const res = await fetch(`${BASE_URL}/support/tickets/${encodeURIComponent(ticketId)}/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ 
        body: text  
      }),
      credentials: 'include'  
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      console.error('❌ Reply failed:', {
        status: res.status,
        statusText: res.statusText,
        error: errorData
      });
      
      if (res.status === 401) {
        showToast('Session expired. Please login again.');
      } else if (res.status === 404) {
        showToast('Ticket not found.');
      } else {
        showToast('Could not send: ' + (errorData.message || res.statusText));
      }
      throw new Error(`HTTP ${res.status}`);
    }
    
    const msg = await res.json();
    state.ticketMessages.push(msg);
    state.ticketReplyText = '';
    showToast('Message sent');
    
  } catch (err) {
    console.error('Send ticket reply error:', err);
  } finally { 
    state.ticketReplySending = false; 
    patchContent(); 
  }
}

/* ─────────────────────────────────────────────
   SHARED UI HELPERS
───────────────────────────────────────────── */
function profileMenuGroup(items) {
  return `
    <div style="background:#fff;border:1.5px solid #e8ecf0;border-radius:14px;overflow:hidden;margin-bottom:20px">
      ${items.join('<div style="height:1px;background:#f0f4f8;margin-left:56px"></div>')}
    </div>`;
}

function profileMenuItem({ icon, label, sublabel, right, onClick, danger, disabled }) {
  return `
    <div onclick="${onClick || ''}"
         style="display:flex;align-items:center;justify-content:space-between;
                padding:15px 18px;cursor:${onClick && !disabled ? 'pointer' : 'default'};
                transition:background 0.1s;"
         onmouseover="this.style.background='${onClick && !disabled ? '#f8fafb' : 'transparent'}'"
         onmouseout="this.style.background='transparent'">
      <div style="display:flex;align-items:${sublabel ? 'flex-start' : 'center'};gap:14px;flex:1">
        <div style="width:36px;height:36px;border-radius:9px;
                    display:flex;align-items:center;justify-content:center;flex-shrink:0">
          ${icon}
        </div>
        <div>
          <div style="font-size:15px;font-weight:600;color:${danger ? '#e24b4a' : disabled ? '#aaa' : '#1a2a2a'}">${escHtml(label)}</div>
          ${sublabel ? `<div style="font-size:12px;color:#888;margin-top:2px;line-height:1.4">${escHtml(sublabel)}</div>` : ''}
        </div>
      </div>
      <div style="color:#bbb;flex-shrink:0">
        ${right || '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>'}
      </div>
    </div>`;
}

function profileBackHeader(title, onBack) {
  return `
    <div style="display:flex;align-items:center;gap:12px;padding:16px 20px 14px;
                border-bottom:1px solid #eaeef2;background:#FBFBFE;sticky;top:0">
      <button onclick="${onBack}" style="background:none;border:none;cursor:pointer;
              padding:6px;border-radius:8px;color:#1D4064;display:flex;align-items:center">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </button>
      <span style="font-size:18px;font-weight:700;color:#1a2a2a">${escHtml(title)}</span>
    </div>`;
}

function profileInput({ label, value, placeholder, type, onInput, error, maxlength, readonly, suffix }) {
  return `
    <div style="margin-bottom:16px">
      <label style="display:block;font-size:12px;font-weight:700;color:#888;letter-spacing:0.06em;
                    text-transform:uppercase;margin-bottom:6px">${escHtml(label)}</label>
      <div style="position:relative">
        <input type="${type || 'text'}"
               value="${escHtml(value || '')}"
               placeholder="${escHtml(placeholder || '')}"
               ${maxlength ? `maxlength="${maxlength}"` : ''}
               ${readonly ? 'readonly' : ''}
               oninput="${onInput || ''}"
               style="width:100%;padding:12px 16px;border:1.5px solid ${error ? '#e24b4a' : '#dde3e8'};
                      border-radius:10px;font-size:15px;color:#1a2a2a;background:${readonly ? '#f8fafb' : '#fff'};
                      outline:none;box-sizing:border-box;font-family:DM Sans,sans-serif"
               onfocus="this.style.borderColor='${error ? '#e24b4a' : '#4b9e95'}'"
               onblur="this.style.borderColor='${error ? '#e24b4a' : '#dde3e8'}'" />
        ${suffix ? `<div style="position:absolute;right:12px;top:50%;transform:translateY(-50%)">${suffix}</div>` : ''}
      </div>
      ${error ? `<div style="color:#e24b4a;font-size:12px;margin-top:4px">${escHtml(error)}</div>` : ''}
    </div>`;
}

function profileSaveBtn(label, onClick, loading) {
  return `
    <button onclick="${onClick}"
            ${loading ? 'disabled' : ''}
            style="width:100%;padding:15px;background:${loading ? '#a8d5d0' : '#FFCC02'};
                   color:#1D4064;border:none;border-radius:12px;font-size:16px;font-weight:700;
                   cursor:${loading ? 'not-allowed' : 'pointer'};font-family:DM Sans,sans-serif;
                   display:flex;align-items:center;justify-content:center;gap:10px;margin-top:8px">
      ${loading ? '<div class="cp-spinner" style="width:20px;height:20px;border-width:2px"></div>' : escHtml(label)}
    </button>`;
}

function purposeChips(value, onChange) {
  return `
    <div style="display:flex;gap:10px;margin-bottom:20px">
      ${['shipping','billing'].map(p => `
        <button onclick="${onChange}('${p}')"
                style="padding:9px 20px;border-radius:20px;border:1.5px solid ${value === p ? '#1D4064' : '#dde3e8'};
                       background:${value === p ? '#E0E9F2' : '#fff'};
                       color:${value === p ? '#1D4064' : '#888'};font-size:14px;font-weight:600;cursor:pointer">
          ${p.charAt(0).toUpperCase() + p.slice(1)}
        </button>`).join('')}
    </div>`;
}

function formatRole(role) {
  return { admin: 'Admin', warehouseAdmin: 'Warehouse Admin', salesRep: 'Sales Representative', customer: 'Customer' }[role] || role || 'Customer';
}

function ticketStatusColor(status) {
  // return { resolved: '#169406', closed: '#C50A00', in_progress: '#4b9e95', open: '#FFCC02' }[status] || '#888';
  return{closed: '#1D4064', resolved:'#1D4064',in_progress: '#169406', open: '#FFCC02'}[status] || '#888';
}

function ticketStatusLabel(status) {
  return { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' }[status] || 'Unknown';
}

/* ─────────────────────────────────────────────
   RENDER: PROFILE (main screen)
───────────────────────────────────────────── */
function renderProfile() {
  if (state.profileSubscreen) return renderProfileSubscreen();

  const user     = state.profileUser || state.customer;
  const name     = user?.name || user?.legalName || 'Customer';
  const email    = user?.email || '';
  const phone    = user?.phone || user?.phoneNumber || '';
  const role     = formatRole(user?.role);
//   const shopName = user?.shopName || '';
const shopName = state.customer.shopName || state.shopDetails?.shopName || '';

  const isAdmin  = user?.role === 'admin' || user?.role === 'warehouseAdmin';

  const svgUser   = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
  const svgStore  = '<svg style="margin-top:6px;" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 22V12h6v10"/></svg>';
  const svgPin    = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>';
  const svgPhone  = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.11 4.18 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.72c.12.9.32 1.78.6 2.64a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.44-1.16a2 2 0 0 1 2.11-.45c.86.28 1.74.48 2.64.6A2 2 0 0 1 22 16.92z"/></svg>';
  const svgKey    = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>';
  const svgTrash  = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e24b4a" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>';
  const svgClip   = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>';
  const svgShield = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>';
  const svgDoc    = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>';
  const svgInfo   = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>';
  const svgLogout = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>';
  const svgChat = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>';

  // Deletion row config
  let deletionLabel  = 'Delete Account';
  let deletionSub    = null;
  let deletionClick  = 'startAccountDeletion()';
  let deletionDisabled = false;
  if (state.deletionLoading) { deletionLabel = 'Delete Account'; deletionDisabled = true; }
  if (state.deletionPending) { deletionLabel = 'Deletion request pending'; deletionSub = 'We will email you when an administrator reviews it.'; deletionClick = null; deletionDisabled = true; }

  return `
    <div class="cp-screen" style="max-width:600px;padding-bottom:40px">

      <!-- ── Avatar & user info ── -->
      <div style="display:flex;flex-direction:column;align-items:center;padding:28px 0 32px">
        <div style="width:90px;height:90px;border-radius:50%;border:3px solid #FFCC02;
                    display:flex;align-items:center;justify-content:center;margin-bottom:16px">
          <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="#FFCC02" stroke-width="1.5">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        ${role !== 'Admin' ? `<p style="font-size:22px;font-weight:700;color:#1D4064;margin:0 0 4px">${escHtml(name)}</p>` : ''}
        ${email ? `<p style="font-size:14px;color:#888;margin:0 0 4px">${escHtml(email)}</p>` : ''}
        ${phone ? `<p style="font-size:14px;color:#888;margin:0 0 8px">${escHtml(phone)}</p>` : ''}
        <span style="background:#E0E9F2;border:1px solid #c5e8e3;color:#1D4064;
                     font-size:12px;font-weight:700;padding:5px 16px;border-radius:20px">${escHtml(role)}</span>
      </div>

      <!-- ── ACCOUNT section ── -->
      <p style="font-size:11px;font-weight:700;color:#aaa;letter-spacing:0.1em;margin:0 0 8px">ACCOUNT AND SUPPORT</p>
      ${profileMenuGroup([
        // shopName ? profileMenuItem({ icon: svgStore, label: 'Your Shop', sublabel: shopName,  onClick: "goProfileSub('shopDetails')"}) : '',
        // profileMenuItem({ icon: svgStore,   label: 'Your shops', sublabel: shopName || 'No Shop Assigned',   onClick: "goProfileSub('shopDetails')" }),

profileMenuItem({ icon: svgClip,   label: 'My support tickets', onClick: "goProfileSub('tickets')" }),
        profileMenuItem({ 
  icon: svgStore, 
  label: 'Your Shop',  
  sublabel: shopName || 'No shop assigned', 
  onClick: "goProfileSub('shopDetails')" 
}),
       
        profileMenuItem({ icon: svgPin,   label: 'Your addresses',     onClick: "goProfileSub('addresses')" }),
        profileMenuItem({ icon: svgPhone, label: 'Your phone numbers', onClick: "goProfileSub('phones')" }),
        profileMenuItem({ icon: svgKey,   label: 'Change Password',    onClick: "goProfileSub('changePassword')" }),
        !isAdmin ? profileMenuItem({
          icon: svgTrash, label: deletionLabel, sublabel: deletionSub,
          onClick: deletionClick, danger: !state.deletionPending, disabled: deletionDisabled,
          right: state.deletionLoading
            ? '<div class="cp-spinner" style="width:16px;height:16px;border-width:2px"></div>'
            : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>',
        }) : '',
      ].filter(Boolean))}

      <!-- ── SUPPORT & LEGAL section ── -->
      <p style="font-size:11px;font-weight:700;color:#aaa;letter-spacing:0.1em;margin:0 0 8px">LEGAL & INQUIRY</p>
      ${profileMenuGroup([
         profileMenuItem({ icon: svgPhone,  label: 'Contact Support',    onClick: "window.location.href='mailto:info@exhaustexpert.com?subject=Exhaust Expert - Support Request'" }),
         profileMenuItem({ icon: svgShield, label: 'Privacy Policy',     onClick: "window.open('/policies/privacy-policy','_blank')" }),
        profileMenuItem({ icon: svgDoc,    label: 'Terms of Use',       onClick: "window.open('/policies/terms-of-service','_blank')" }),
      ])}

     

      <!-- ── Logout button ── -->
      <button onclick="confirmLogout()"
              style="width:100%;padding:16px;background:#C50A00;color:#fff;border:none;
                     border-radius:12px;font-size:16px;font-weight:700;cursor:pointer;
                     display:flex;align-items:center;justify-content:center;gap:10px;
                     box-shadow:0 4px 16px rgba(226,75,74,0.3);font-family:DM Sans,sans-serif">
        ${svgLogout} Logout
      </button>

      <!-- Developed By -->
      <div style="text-align:center;margin-top:24px;padding-bottom:8px">
        <a href="https://radiatechs.com" target="_blank"
           style="font-size:12px;color:#aaa;text-decoration:underline;font-weight:500"
           onmouseover="this.style.color='#B27B68'" onmouseout="this.style.color='#B27B68'">
          Developed by Radiance Technologies
        </a>
      </div>
    </div>

    <!-- Logout confirm dialog -->
    ${state.logoutDialogOpen ? `
      <div style="position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:4000;
                  display:flex;align-items:center;justify-content:center">
        <div style="background:#fff;border-radius:16px;padding:28px 24px;width:88%;max-width:380px;
                    box-shadow:0 8px 32px rgba(0,0,0,0.18)">
          <p style="font-size:18px;font-weight:700;color:#1a2a2a;margin:0 0 8px">Logout</p>
          <p style="font-size:14px;color:#888;margin:0 0 24px">Are you sure you want to logout?</p>
          <div style="display:flex;gap:12px">
            <button onclick="state.logoutDialogOpen=false;patchContent()"
                    style="flex:1;padding:13px;border:1.5px solid #dde3e8;border-radius:10px;
                           background:#fff;font-size:15px;font-weight:600;color:#888;cursor:pointer">Cancel</button>
            <button onclick="doLogout()"
                    style="flex:1;padding:13px;border:none;border-radius:10px;
                           background:#e24b4a;font-size:15px;font-weight:700;color:#fff;cursor:pointer">Logout</button>
          </div>
        </div>
      </div>` : ''}
  `;
}

function goProfileSub(sub) {
  state.profileSubscreen = sub;
  if (sub === 'addresses') loadAddresses();
  else if (sub === 'phones') loadPhones();
  else if (sub === 'tickets') loadTickets();
  else if (sub === 'shopDetails') loadShopDetails();
  else patchContent();
}

function confirmLogout() {
  state.logoutDialogOpen = true;
  patchContent();
}

function doLogout() {
  state.logoutDialogOpen = false;
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('customerData');
  showToast('Logged out');
  setTimeout(() => window.location.href = '/pages/login', 800);
}

/* ─────────────────────────────────────────────
   SHOP DETAILS
───────────────────────────────────────────── */
async function loadShopDetails() {
  const shopId = state.profileUser?.shopId || state.customer?.shopId;
  if (!shopId) {
    state.shopDetailsError = 'No shop assigned';
    state.shopDetailsLoading = false;
    patchContent();
    return;
  }
  
  state.shopDetailsLoading = true;
  state.shopDetailsError = null;
  patchContent();
  
  try {
    const data = await apiFetch(`${BASE_URL}/shops/${shopId}`);
    const shop = Array.isArray(data) ? data[0] : data;
    
    state.shopDetails = shop;
    
    if (shop?.shopName) {
      state.customer.shopName = shop.shopName;
      state.profileUser.shopName = shop.shopName;  
    }
  } catch (e) {
    console.warn('Could not load shop details:', e.message);
    state.shopDetailsError = e.message || 'Failed to load shop';
  } finally {
    state.shopDetailsLoading = false;
    patchContent();
  }
}

/* ─────────────────────────────────────────────
   RENDER: SHOP DETAILS SCREEN
───────────────────────────────────────────── */
function renderShopDetailsScreen() {
  const svgStore = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 22V12h6v10"/></svg>';
  
  const shop = state.shopDetails;
  
  return `
    <div style="max-width:600px;margin:0 auto">
      ${profileBackHeader('Shop Details', "state.profileSubscreen=null;patchContent()")}
      
      <div style="padding:20px">
        ${state.shopDetailsLoading 
          ? '<div class="cp-loading"><div class="cp-spinner"></div>Loading shop details…</div>' 
          : state.shopDetailsError 
            ? `<div style="text-align:center;padding:30px;color:#e24b4a">${escHtml(state.shopDetailsError)}<br><button class="cp-retry-btn" style="margin-top:12px" onclick="loadShopDetails()">Retry</button></div>`
            : !shop 
              ? `<div style="text-align:center;padding:50px 20px;color:#888"><p>No shop data available</p></div>`
              : `
        <div style="background:#fff;border:1.5px solid #eaeef2;border-radius:14px;overflow:hidden">
          
          <!-- Shop Name -->
          <div style="padding:16px 18px;border-bottom:1px solid #f0f4f8">
            <div style="display:flex;gap:14px;align-items:flex-start">
              <div style="width:36px;height:36px;border-radius:9px;margin-top: 6px;display:flex;align-items:center;justify-content:center;flex-shrink:0">
                ${svgStore}
              </div>
              <div style="flex:1">
                <p style="font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:4px">Shop Name</p>
                <p style="font-size:15px;font-weight:600;color:#1a2a2a;margin:0">${escHtml(shop.shopName || '—')}</p>
              </div>
            </div>
          </div>
          
          <!-- Warehouse -->
          <div style="padding:16px 18px;border-bottom:1px solid #f0f4f8">
            <div style="display:flex;gap:14px;align-items:flex-start">
              <div style="width:36px;height:36px;border-radius:9px;margin-top:6px;display:flex;align-items:center;justify-content:center;flex-shrink:0">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
              </div>
              <div style="flex:1">
                <p style="font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:4px">Warehouse</p>
                <p style="font-size:15px;font-weight:600;color:#1a2a2a;margin:0">${escHtml(shop.warehouseId?.name || shop.warehouseName || '—')}</p>
              </div>
            </div>
          </div>
          
          <!-- Phone -->
          <div style="padding:16px 18px;border-bottom:1px solid #f0f4f8">
            <div style="display:flex;gap:14px;align-items:flex-start">
              <div style="width:36px;height:36px;border-radius:9px;margin-top:6px;display:flex;align-items:center;justify-content:center;flex-shrink:0">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.11 4.18 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.72c.12.9.32 1.78.6 2.64a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.44-1.16a2 2 0 0 1 2.11-.45c.86.28 1.74.48 2.64.6A2 2 0 0 1 22 16.92z"/></svg>
              </div>
              <div style="flex:1">
                <p style="font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:4px">Shop Phone</p>
                <p style="font-size:15px;font-weight:600;color:#1a2a2a;margin:0">${escHtml(formatPhoneDisplay(shop.phoneNumber) || '—')}</p>
              </div>
            </div>
          </div>
          
          <!-- Email -->
          <div style="padding:16px 18px;border-bottom:1px solid #f0f4f8">
            <div style="display:flex;gap:14px;align-items:flex-start">
              <div style="width:36px;height:36px;border-radius:9px;margin-top:6px;display:flex;align-items:center;justify-content:center;flex-shrink:0">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </div>
              <div style="flex:1">
                <p style="font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:4px">Shop Email</p>
                <p style="font-size:15px;font-weight:600;color:#1a2a2a;margin:0">${escHtml(shop.email || '—')}</p>
              </div>
            </div>
          </div>
          
          <!-- Primary Address -->
          <div style="padding:16px 18px">
            <div style="display:flex;gap:14px;align-items:flex-start">
              <div style="width:36px;height:36px;border-radius:9px;margin-top:6px;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>
              <div style="flex:1">
                <p style="font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:4px">Primary Address</p>
                <p style="font-size:15px;font-weight:600;color:#1a2a2a;margin:0;line-height:1.5">${escHtml(shop.physicalLocation || shop.primaryAddress || '—')}</p>
              </div>
            </div>
          </div>
          
          <!-- Additional Addresses -->
          ${shop.additionalAddresses?.length > 0 ? `
            <div style="border-top:1px solid #f0f4f8;padding:16px 18px">
              <p style="font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:12px">Additional Addresses</p>
              ${shop.additionalAddresses.slice(0, 2).map((addr, i) => `
                <p style="font-size:14px;color:#1a2a2a;margin:${i > 0 ? '8px 0 0' : '0'};line-height:1.4">• ${escHtml(addr)}</p>
              `).join('')}
            </div>` : ''}
          
        </div>
    
        `}
      </div>
    </div>
  `;
}

/* ─────────────────────────────────────────────
   RENDER: SUB-SCREEN ROUTER
───────────────────────────────────────────── */
function renderProfileSubscreen() {
  switch (state.profileSubscreen) {
    case 'addresses':      return renderAddressesScreen();
    case 'addAddress':     return renderAddressForm(false);
    case 'editAddress':    return renderAddressForm(true);
    case 'phones':         return renderPhonesScreen();
    case 'addPhone':       return renderPhoneForm(false);
    case 'editPhone':      return renderPhoneForm(true);
    case 'tickets':        return renderTicketsScreen();
    case 'ticketDetail':   return renderTicketDetailScreen();
    case 'newTicket': return renderNewTicketScreen();
    case 'changePassword': return renderChangePasswordScreen();
    case 'deleteAccount':  return renderDeleteAccountScreen();
    case 'shopDetails':    return renderShopDetailsScreen();
    default: state.profileSubscreen = null; return renderProfile();
  }
}

/* ─────────────────────────────────────────────
   RENDER: YOUR ADDRESSES
───────────────────────────────────────────── */
function renderAddressesScreen() {
  const svgPin = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>';

  return `
    <div style="max-width:600px;margin:0 auto">
      ${profileBackHeader('Your addresses', "state.profileSubscreen=null;patchContent()")}

      <div style="padding:20px">
        <!-- Add button -->
        <button onclick="openAddAddress('shipping')"
                style="display:flex;align-items:center;justify-content:center;gap:8px;
                       width:100%;padding:13px;border:1.5px dashed #1D4064;border-radius:12px;
                       background:#fff;color:#1D4064;font-size:15px;font-weight:600;
                       cursor:pointer;margin-bottom:20px">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add address
        </button>

        ${state.addressesLoading ? '<div class="cp-loading"><div class="cp-spinner"></div>Loading addresses…</div>' :
          state.addressesError   ? `<div style="text-align:center;padding:30px;color:#e24b4a">${escHtml(state.addressesError)}<br><button class="cp-retry-btn" style="margin-top:12px" onclick="loadAddresses()">Retry</button></div>` :
          state.addresses.length === 0 ? `
            <div style="text-align:center;padding:50px 20px;color:#888">
              ${svgPin.replace('width="20"','width="48"').replace('height="20"','height="48"').replace('stroke="#1D4064"','stroke="#ccc"')}
              <p style="font-size:17px;font-weight:600;color:#1a2a2a;margin:16px 0 8px">No saved addresses</p>
              <p style="font-size:14px">Tap the button above to add a shipping or billing address.</p>
            </div>` :
          `<div style="background:#fff;border:1.5px solid #eaeef2;border-radius:14px;overflow:hidden">
            ${state.addresses.map((addr, i) => `
              ${i > 0 ? '<div style="height:1px;background:#f0f4f8"></div>' : ''}
              <div style="padding:16px 18px;display:flex;align-items:flex-start;justify-content:space-between">
                <div style="display:flex;gap:14px;flex:1">
                  <div style="width:36px;height:36px;border-radius:9px;
                              display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px">
                    ${svgPin}
                  </div>
                  <div style="flex:1">
                    <span style="display:inline-block;font-size:11px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;
                                 padding:2px 10px;border-radius:6px;margin-bottom:6px;
                                 background:${addr.addressPurpose === 'billing' ? 'rgba(107,114,128,0.12)' : '#FFCC02'};
                                 color:${addr.addressPurpose === 'billing' ? '#1D4064' : '#1D4064'}">
                      ${addr.addressPurpose === 'billing' ? 'Billing' : 'Shipping'}
                    </span>
                    <p style="font-size:15px;font-weight:600;color:#1a2a2a;margin:0;line-height:1.4">
                      ${escHtml([addr.addressLine1, addr.addressLine2, addr.city, addr.state, addr.postalCode].filter(Boolean).join(', '))}
                    </p>
                  </div>
                </div>
                <div style="display:flex;gap:8px;flex-shrink:0;padding-top:2px">
                  <button onclick="openEditAddress(${JSON.stringify(addr).replace(/"/g,'&quot;')})"
                          style="width:34px;height:34px;border:1.5px solid #dde3e8;border-radius:8px;
                                 background:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B27B68" stroke-width="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  <button onclick="deleteAddress('${addr._id}')"
                          style="width:34px;height:34px;border:1.5px solid #fce8e8;border-radius:8px;
                                 background:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e24b4a" stroke-width="2">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                    </svg>
                  </button>
                </div>
              </div>`).join('')}
          </div>`}
     </div>
    </div>

    ${state.deleteAddressConfirmId ? `
      <div style="position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:4000;
                  display:flex;align-items:center;justify-content:center"
           onclick="cancelDeleteAddress()">
        <div style="background:#fff;border-radius:16px;padding:28px 24px;width:88%;max-width:360px;
                    box-shadow:0 8px 32px rgba(0,0,0,0.18)" onclick="event.stopPropagation()">
          <p style="font-size:18px;font-weight:700;color:#1a2a2a;margin:0 0 8px">Delete address?</p>
          <p style="font-size:14px;color:#888;margin:0 0 24px;line-height:1.5">
            Are you sure you want to delete this address?
          </p>
          <div style="display:flex;gap:12px">
            <button onclick="cancelDeleteAddress()"
                    style="flex:1;padding:13px;border:1.5px solid #dde3e8;border-radius:10px;
                           background:#fff;font-size:15px;font-weight:600;color:#888;cursor:pointer">
              Cancel
            </button>
            <button onclick="confirmDeleteAddress()"
                    style="flex:1;padding:13px;border:none;border-radius:10px;
                           background:#e24b4a;font-size:15px;font-weight:700;color:#fff;cursor:pointer">
              Delete
            </button>
          </div>
        </div>
      </div>` : ''}
    `;
}
/* ─────────────────────────────────────────────
   RENDER: ADDRESS FORM (add / edit)
───────────────────────────────────────────── */
function renderAddressForm(isEdit) {
  const f = state.addrForm;
  const back = isEdit ? "state.profileSubscreen='addresses';patchContent()" : "state.profileSubscreen='addresses';patchContent()";
  return `
    <div style="max-width:600px;margin:0 auto">
      ${profileBackHeader(isEdit ? 'Edit address' : 'Add address', back)}
      <div style="padding:20px">
        <p style="font-size:13px;color:#888;margin:0 0 16px;line-height:1.6">
          ${f.purpose === 'billing' ? 'Billing addresses are used for invoices and accounts payable.' : 'Shipping addresses are used for delivery (ship-to).'}
        </p>

        <!-- Purpose chips -->
        <label style="font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px;display:block">Address type</label>
        ${purposeChips(f.purpose, "updateAddrPurpose")}

        ${profileInput({ label:'Street address', value:f.line1, placeholder:'Address line 1', error:f.errors.line1, maxlength:500,
          onInput:`state.addrForm.line1=this.value;state.addrForm.errors.line1=null;` })}
        ${profileInput({ label:'Address line 2 (optional)', value:f.line2, placeholder:'Apt, suite, unit…', error:f.errors.line2, maxlength:200,
          onInput:`state.addrForm.line2=this.value;` })}
        ${profileInput({ label:'City', value:f.city, placeholder:'City', error:f.errors.city, maxlength:100,
          onInput:`state.addrForm.city=this.value;state.addrForm.errors.city=null;` })}

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div>
            ${profileInput({ label:'State', value:f.stateCode, placeholder:'CA', error:f.errors.stateCode, maxlength:2,
              onInput:`state.addrForm.stateCode=this.value.toUpperCase();state.addrForm.errors.stateCode=null;` })}
          </div>
          <div>
            ${profileInput({ label:'ZIP code', value:f.zip, placeholder:'12345', error:f.errors.zip, maxlength:10, type:'tel',
              onInput:`state.addrForm.zip=this.value;state.addrForm.errors.zip=null;` })}
          </div>
        </div>

        ${profileSaveBtn(isEdit ? 'Save changes' : 'Add address', 'saveAddress()', f.saving)}
      </div>
    </div>`;
}

function updateAddrPurpose(p) {
  state.addrForm.purpose = p;
  patchContent();
}

/* ─────────────────────────────────────────────
   RENDER: YOUR PHONES
───────────────────────────────────────────── */
function renderPhonesScreen() {
  const svgPhone = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.11 4.18 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.72c.12.9.32 1.78.6 2.64a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.44-1.16a2 2 0 0 1 2.11-.45c.86.28 1.74.48 2.64.6A2 2 0 0 1 22 16.92z"/></svg>';

  return `
    <div style="max-width:600px;margin:0 auto">
      ${profileBackHeader('Your phone numbers', "state.profileSubscreen=null;patchContent()")}
      <div style="padding:20px">
        <button onclick="openAddPhone()"
                style="display:flex;align-items:center;justify-content:center;gap:8px;
                       width:100%;padding:13px;border:1.5px dashed #1D4064;border-radius:12px;
                       background:#fff;color:#1D4064;font-size:15px;font-weight:600;
                       cursor:pointer;margin-bottom:20px">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2.5">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add phone number
        </button>

        ${state.phonesLoading ? '<div class="cp-loading"><div class="cp-spinner"></div>Loading…</div>' :
          state.phonesError   ? `<div style="text-align:center;padding:30px;color:#e24b4a">${escHtml(state.phonesError)}<br><button class="cp-retry-btn" style="margin-top:12px" onclick="loadPhones()">Retry</button></div>` :
          state.phones.length === 0 ? `
            <div style="text-align:center;padding:50px 20px;color:#888">
              <p style="font-size:17px;font-weight:600;color:#1a2a2a;margin:0 0 8px">No saved phone numbers</p>
              <p style="font-size:14px">Tap the button above to add a shipping contact number.</p>
            </div>` :
          `<div style="background:#fff;border:1.5px solid #eaeef2;border-radius:14px;overflow:hidden">
            ${state.phones.map((p, i) => `
              ${i > 0 ? '<div style="height:1px;background:#f0f4f8"></div>' : ''}
              <div style="padding:16px 18px;display:flex;align-items:center;justify-content:space-between">
                <div style="display:flex;gap:14px;align-items:center;flex:1">
                  <div style="width:36px;height:36px;border-radius:9px;
                              display:flex;align-items:center;justify-content:center;flex-shrink:0">
                    ${svgPhone}
                  </div>
                  <div>
                    <span style="display:inline-block;font-size:11px;font-weight:700;letter-spacing:0.05em;
                                 text-transform:uppercase;padding:2px 10px;border-radius:6px;
                                 background:#FFCC02;color:#1D4064;margin-bottom:4px">Shipping</span>
                    <p style="font-size:15px;font-weight:600;color:#1a2a2a;margin:0">${escHtml(formatPhoneDisplay((p.phone||'').replace(/\D/g,'').slice(-10)))}</p>
                  </div>
                </div>
                <div style="display:flex;gap:8px;flex-shrink:0">
                  <button onclick="openEditPhone(${JSON.stringify(p).replace(/"/g,'&quot;')})"
                          style="width:34px;height:34px;border:1.5px solid #dde3e8;border-radius:8px;
                                 background:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B27B68" stroke-width="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  <button onclick="deletePhone('${p._id}')"
                          style="width:34px;height:34px;border:1.5px solid #fce8e8;border-radius:8px;
                                 background:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e24b4a" stroke-width="2">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                    </svg>
                  </button>
                </div>
              </div>`).join('')}
          </div>`}
     </div>
    </div>

    ${state.deletePhoneConfirmId ? `
      <div style="position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:4000;
                  display:flex;align-items:center;justify-content:center"
           onclick="cancelDeletePhone()">
        <div style="background:#fff;border-radius:16px;padding:28px 24px;width:88%;max-width:360px;
                    box-shadow:0 8px 32px rgba(0,0,0,0.18)" onclick="event.stopPropagation()">
          <p style="font-size:18px;font-weight:700;color:#1a2a2a;margin:0 0 8px">Delete phone number?</p>
          <p style="font-size:14px;color:#888;margin:0 0 24px;line-height:1.5">
            Are you sure you want to delete this phone number?
          </p>
          <div style="display:flex;gap:12px">
            <button onclick="cancelDeletePhone()"
                    style="flex:1;padding:13px;border:1.5px solid #dde3e8;border-radius:10px;
                           background:#fff;font-size:15px;font-weight:600;color:#888;cursor:pointer">
              Cancel
            </button>
            <button onclick="confirmDeletePhone()"
                    style="flex:1;padding:13px;border:none;border-radius:10px;
                           background:#e24b4a;font-size:15px;font-weight:700;color:#fff;cursor:pointer">
              Delete
            </button>
          </div>
        </div>
      </div>` : ''}
    `;
}

/* ─────────────────────────────────────────────
   RENDER: PHONE FORM
───────────────────────────────────────────── */
function renderPhoneForm(isEdit) {
  const f = state.phoneForm;
  const back = "state.profileSubscreen='phones';patchContent()";
  return `
    <div style="max-width:600px;margin:0 auto">
      ${profileBackHeader(isEdit ? 'Edit phone number' : 'Add phone number', back)}
      <div style="padding:20px">
        <label style="display:block;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px">Phone number *</label>
        <div style="position:relative;margin-bottom:4px">
          <input type="tel" maxlength="14"
                 value="${escHtml(formatPhoneDisplay((f.phone||'').replace(/\D/g,'').slice(-10)))}"
                 placeholder="(555) 123-4567"
                 oninput="state.phoneForm.phone=this.value.replace(/\\D/g,'').slice(0,10);state.phoneForm.errors.phone=null;this.value=formatPhoneDisplay(state.phoneForm.phone)"
                 style="width:100%;padding:13px 16px;border:1.5px solid ${f.errors.phone ? '#e24b4a' : '#dde3e8'};
                        border-radius:10px;font-size:16px;color:#1a2a2a;outline:none;box-sizing:border-box;
                        font-family:DM Sans,sans-serif"
                 onfocus="this.style.borderColor='#4b9e95'" onblur="this.style.borderColor='${f.errors.phone ? '#e24b4a' : '#dde3e8'}'" />
        </div>
        ${f.errors.phone ? `<div style="color:#e24b4a;font-size:12px;margin-bottom:12px">${escHtml(f.errors.phone)}</div>` : '<div style="height:12px"></div>'}
        <p style="font-size:13px;color:#888;margin:0 0 24px;line-height:1.5">This number is saved for shipping contact during checkout.</p>
        ${profileSaveBtn(isEdit ? 'Save changes' : 'Add phone number', 'savePhone()', f.saving)}
      </div>
    </div>`;
}

/* ─────────────────────────────────────────────
   RENDER: SUPPORT TICKETS LIST
───────────────────────────────────────────── */
function renderTicketsScreen() {
  function formatTicketDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
  }

  return `
    <div style="max-width:700px;margin:0 auto">
      ${profileBackHeader('My support tickets', "state.profileSubscreen=null;patchContent()")}
      <div style="padding:20px">

        ${state.ticketsLoading ? '<div class="cp-loading"><div class="cp-spinner"></div>Loading tickets…</div>' :
          state.ticketsError   ? `<div style="text-align:center;padding:30px;color:#e24b4a">${escHtml(state.ticketsError)}<br><button class="cp-retry-btn" style="margin-top:12px" onclick="loadTickets()">Retry</button></div>` :
          state.tickets.length === 0 ? `
            <div style="text-align:center;padding:60px 20px">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1.5" style="margin-bottom:16px">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <p style="font-size:18px;font-weight:700;color:#1a2a2a;margin:0 0 8px">No support tickets yet</p>
              <p style="font-size:14px;color:#888">Contact support using the button below.</p>
            </div>` :
          `<div style="display:flex;flex-direction:column;gap:12px">
            ${state.tickets.map(t => `
              <div onclick="openTicketDetail('${t.id}')"
                   style="background:#fff;border:1.5px solid #eaeef2;border-radius:12px;padding:18px;
                          cursor:pointer;transition:border-color 0.15s"
                   onmouseover="this.style.borderColor='#1D4064'" onmouseout="this.style.borderColor='#eaeef2'">
                <p style="font-size:16px;font-weight:600;color:#1a2a2a;margin:0 0 10px;line-height:1.4">
                  ${escHtml(t.subject || t.description?.slice(0,60) || 'Support request')}
                </p>
                <div style="display:flex;align-items:center;justify-content:space-between">
                  <span style="font-size:12px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;
                               padding:5px 14px;border-radius:20px;border:1px solid #eaeef2;
                               color:#fff;background:${ticketStatusColor(t.status)}">
                    ${ticketStatusLabel(t.status)}
                  </span>
                  <span style="font-size:12px;color:#888">${formatTicketDate(t.createdAt)}</span>
                </div>
              </div>`).join('')}
          </div>`}

        <!-- Contact support CTA -->
        <button onclick="state.profileSubscreen='newTicket';patchContent()"
        style="display:flex;align-items:center;justify-content:center;gap:10px;
               width:100%;padding:14px;background:#FFCC02;color:#1D4064;border:none;
               border-radius:12px;font-size:15px;font-weight:700;cursor:pointer;margin-top:20px;
               font-family:DM Sans,sans-serif">
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2.5">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
  New support ticket
</button>
      </div>
    </div>`;
}

async function openTicketDetail(id) {
  state.currentTicket    = null;
  state.ticketMessages   = [];
  state.ticketReplyText  = '';
  state.profileSubscreen = 'ticketDetail';
  patchContent();
  await loadTicketDetail(id);
}

/* ─────────────────────────────────────────────
   RENDER: TICKET DETAIL
───────────────────────────────────────────── */
function renderTicketDetailScreen() {
  const t = state.currentTicket;

  if (state.ticketMessagesLoading && !t) {
    return `
      <div style="max-width:700px;margin:0 auto">
        ${profileBackHeader('Support ticket', "state.profileSubscreen='tickets';patchContent()")}
        <div class="cp-loading"><div class="cp-spinner"></div>Loading…</div>
      </div>`;
  }

  if (!t) {
    return `
      <div style="max-width:700px;margin:0 auto">
        ${profileBackHeader('Support ticket', "state.profileSubscreen='tickets';patchContent()")}
        <div style="padding:20px;text-align:center;color:#e24b4a">Could not load ticket</div>
      </div>`;
  }

  const canReply = ['open','in_progress'].includes(t.status);

  return `
    <div style="max-width:700px;margin:0 auto">
      ${profileBackHeader('Support ticket', "state.profileSubscreen='tickets';patchContent()")}
      <div style="padding:20px">

        <!-- Ticket header card -->
        <div style="background:#fff;border:1.5px solid #eaeef2;border-radius:12px;padding:18px;margin-bottom:20px">
          <div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:8px">
            <p style="font-size:16px;font-weight:700;color:#1a2a2a;margin:0;flex:1;padding-right:12px">
             #${escHtml(t.ticketNumber || t.id || 'Support request')}
            </p>
            <span style="font-size:11px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;
                         padding:4px 12px;border-radius:20px;flex-shrink:0;
                         color:#fff;background:${ticketStatusColor(t.status)}">
              ${ticketStatusLabel(t.status)}
            </span>
          </div>
          ${t.description ? `<p style="font-size:14px;color:#555;margin:0;line-height:1.6">${escHtml(t.description)}</p>` : ''}
        </div>

        <!-- Messages -->
        <p style="font-size:13px;font-weight:700;color:#aaa;letter-spacing:0.08em;text-transform:uppercase;margin:0 0 12px">Conversation</p>

        ${state.ticketMessages.length === 0 ? '<p style="color:#888;font-size:14px;font-style:italic;padding:8px">No messages yet.</p>' :
          `<div style="display:flex;flex-direction:column;gap:10px;margin-bottom:16px;max-height:420px;overflow-y:auto">
            ${state.ticketMessages.map(m => {
              const isCustomer = m.senderType === 'customer' || m.sender === 'customer';
              return `
                <div style="display:flex;justify-content:${isCustomer ? 'flex-end' : 'flex-start'}">
                  <div style="max-width:82%;padding:11px 14px;border-radius:14px;
                              ${isCustomer ? 'background:#FEF4BE;border-bottom-right-radius:4px' : 'background:#f3f4f6;border-bottom-left-radius:4px'}">
                    <p style="font-size:11px;font-weight:700;color:#888;margin:0 0 4px">${isCustomer ? 'You' : 'Support'}</p>
                    <p style="font-size:14px;color:#1e3a5f;margin:0;line-height:1.45">${escHtml(m.body || m.text || m.message || '')}</p>
                    ${m.createdAt ? `<p style="font-size:11px;color:#aaa;margin-top:6px">${new Date(m.createdAt).toLocaleDateString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</p>` : ''}
                  </div>
                </div>`;
            }).join('')}
          </div>`}

        <!-- Reply input -->
        ${canReply ? `
          <div style="border-top:1px solid #eaeef2;padding-top:16px;display:flex;gap:10px;align-items:flex">
            <textarea id="ticket-reply-input"
                      placeholder="Type a message…"
                      oninput="state.ticketReplyText=this.value;this.style.height='auto';this.style.height=this.scrollHeight+'px';return true;"
                      style="flex:1;min-height:44px;max-height:120px;padding:11px 14px;
                             border:1.5px solid #dde3e8;border-radius:12px;font-size:14px;
                             color:#1a2a2a;resize:none;outline:none;font-family:DM Sans,sans-serif"
                      onfocus="this.style.borderColor='#FFCC02'" onblur="this.style.borderColor='#dde3e8'">${escHtml(state.ticketReplyText)}</textarea>
            <button onclick="sendTicketReply('${t.id}')"
                    ${state.ticketReplySending ? 'disabled' : ''}
                    style="width:44px;height:44px;margin-top:8px;border-radius:12px;background:#FFCC02;border:none;
                           cursor:pointer;display:flex;align-items:center;justify-content:center;color:#fff">
              ${state.ticketReplySending
                ? '<div class="cp-spinner" style="width:18px;height:18px;border-width:2px"></div>'
                : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2" style="transform:rotate(90deg)"><path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>'}
            </button>
          </div>` : `
          <div style="padding:12px;background:#f8fafb;border-radius:10px;text-align:center">
            <p style="font-size:13px;color:#888;margin:0">This ticket is ${ticketStatusLabel(t.status).toLowerCase()} — no further replies needed.</p>
          </div>`}
      </div>
    </div>`;
}

/* ─────────────────────────────────────────────
   RENDER: CHANGE PASSWORD
───────────────────────────────────────────── */
function renderChangePasswordScreen() {
  function pwEye(show, toggle) {
    return `<button type="button" onclick="${toggle}" style="background:none;border:none;cursor:pointer;padding:4px;color:#888">
      ${show
        ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>'
        : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>'}
    </button>`;
  }

  return `
    <div style="max-width:500px;margin:0 auto">
      ${profileBackHeader('Change Password', "state.profileSubscreen=null;patchContent()")}
      <div style="padding:20px">

        <label style="display:block;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px">Current password</label>
        <div style="position:relative;margin-bottom:16px">
          <input type="${state.cpShowOld ? 'text' : 'password'}" value="${escHtml(state.cpOldPw)}"
                 oninput="state.cpOldPw=this.value;state.cpError=''"
                 placeholder="Enter current password"
                 style="width:100%;padding:12px 48px 12px 16px;border:1.5px solid #dde3e8;border-radius:10px;
                        font-size:15px;color:#1a2a2a;outline:none;box-sizing:border-box;font-family:DM Sans,sans-serif"
                 onfocus="this.style.borderColor='#4b9e95'" onblur="this.style.borderColor='#dde3e8'">
          <div style="position:absolute;right:10px;top:50%;transform:translateY(-50%)">
            ${pwEye(state.cpShowOld, "state.cpShowOld=!state.cpShowOld;patchContent()")}
          </div>
        </div>

        <label style="display:block;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px">New password</label>
        <div style="position:relative;margin-bottom:16px">
          <input type="${state.cpShowNew ? 'text' : 'password'}" value="${escHtml(state.cpNewPw)}"
                 oninput="state.cpNewPw=this.value;state.cpError=''"
                 placeholder="Min 8 characters"
                 style="width:100%;padding:12px 48px 12px 16px;border:1.5px solid #dde3e8;border-radius:10px;
                        font-size:15px;color:#1a2a2a;outline:none;box-sizing:border-box;font-family:DM Sans,sans-serif"
                 onfocus="this.style.borderColor='#4b9e95'" onblur="this.style.borderColor='#dde3e8'">
          <div style="position:absolute;right:10px;top:50%;transform:translateY(-50%)">
            ${pwEye(state.cpShowNew, "state.cpShowNew=!state.cpShowNew;patchContent()")}
          </div>
        </div>

        <label style="display:block;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px">Confirm new password</label>
        <div style="position:relative;margin-bottom:8px">
          <input type="${state.cpShowConfirm ? 'text' : 'password'}" value="${escHtml(state.cpConfirmPw)}"
                 oninput="state.cpConfirmPw=this.value;state.cpError=''"
                 onkeydown="if(event.key==='Enter')submitChangePassword()"
                 placeholder="Repeat new password"
                 style="width:100%;padding:12px 48px 12px 16px;border:1.5px solid #dde3e8;border-radius:10px;
                        font-size:15px;color:#1a2a2a;outline:none;box-sizing:border-box;font-family:DM Sans,sans-serif"
                 onfocus="this.style.borderColor='#4b9e95'" onblur="this.style.borderColor='#dde3e8'">
          <div style="position:absolute;right:10px;top:50%;transform:translateY(-50%)">
            ${pwEye(state.cpShowConfirm, "state.cpShowConfirm=!state.cpShowConfirm;patchContent()")}
          </div>
        </div>

        ${state.cpError ? `<div style="color:#e24b4a;font-size:13px;margin-bottom:12px;padding:10px 14px;background:#fef2f2;border-radius:8px">${escHtml(state.cpError)}</div>` : '<div style="height:16px"></div>'}
        ${profileSaveBtn('Change password', 'submitChangePassword()', state.cpLoading)}
      </div>
    </div>`;
}

/* ─────────────────────────────────────────────
   RENDER: DELETE ACCOUNT (OTP step)
───────────────────────────────────────────── */
function renderDeleteAccountScreen() {
  const user  = state.profileUser || state.customer;
  const email = user?.email || '';

  return `
    <div style="max-width:480px;margin:0 auto">
      ${profileBackHeader('Delete Account', "state.profileSubscreen=null;state.deletionOtpStep=false;patchContent()")}
      <div style="padding:24px 20px">
        <div style="background:#fef2f2;border:1.5px solid #fca5a5;border-radius:12px;padding:18px;margin-bottom:24px">
          <p style="font-weight:700;font-size:15px;color:#991b1b;margin:0 0 6px">⚠️ Confirm account deletion</p>
          <p style="font-size:13px;color:#991b1b;margin:0;line-height:1.6">
            A 6-digit code was sent to <strong>${escHtml(email)}</strong>.<br>
            Enter it below to submit your deletion request to an administrator.
          </p>
        </div>

        <label style="display:block;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:8px">Verification code</label>
        <input type="tel" maxlength="6"
               value="${escHtml(state.deletionOtpCode)}"
               placeholder="000000"
               oninput="state.deletionOtpCode=this.value.replace(/\\D/g,'').slice(0,6)"
               style="width:100%;padding:14px 16px;border:1.5px solid #dde3e8;border-radius:10px;
                      font-size:22px;letter-spacing:0.3em;text-align:center;color:#1a2a2a;
                      outline:none;box-sizing:border-box;font-family:DM Sans,sans-serif;margin-bottom:20px"
               onfocus="this.style.borderColor='#e24b4a'" onblur="this.style.borderColor='#dde3e8'">

        <button onclick="verifyDeletionOtp()"
                ${state.deletionOtpVerifying ? 'disabled' : ''}
                style="width:100%;padding:15px;background:${state.deletionOtpVerifying ? '#f87171' : '#e24b4a'};
                       color:#fff;border:none;border-radius:12px;font-size:16px;font-weight:700;
                       cursor:pointer;display:flex;align-items:center;justify-content:center;
                       font-family:DM Sans,sans-serif;margin-bottom:14px">
          ${state.deletionOtpVerifying ? '<div class="cp-spinner" style="width:20px;height:20px;border-width:2px"></div>' : 'Confirm deletion request'}
        </button>

        <button onclick="resendDeletionOtp()"
                style="width:100%;padding:13px;background:none;border:1.5px solid #dde3e8;
                       border-radius:12px;font-size:15px;font-weight:600;color:#888;cursor:pointer">
          Resend code
        </button>
      </div>
    </div>`;
}

async function resendDeletionOtp() {
  try {
    await apiFetch(`${BASE_URL}/users/request-deletion-otp`, { method: 'POST' });
    showToast('Code resent — check your email');
  } catch (e) {
    showToast(e.message || 'Could not resend code');
  }
}

/* ─────────────────────────────────────────────
   INIT — load profile user on page load
───────────────────────────────────────────── */

(async function initProfileModule() {
  await loadProfileUser();
  await loadDeletionStatus();
  if (state.customer?.shopId) {
    await loadShopDetails();
  }
})();

/* ─────────────────────────────────────────────
PART SEARCH API
───────────────────────────────────────────── */
async function searchPartsByTerm(term) {
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`${BASE_URL}/vehicle/product-search-warehouse?term=${encodeURIComponent(term)}`,
     {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      credentials: 'include'
    });
    
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    
    return Array.isArray(data) 
      ? data 
      : (data.products || data.results || data.data || []);
  } catch (err) {
    console.error('Part search error:', err);
    throw err;
  }
}


async function loadNotifications() {
  state.notificationsLoading = true;
  state.notificationsError = null;
  patchContent();

  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');

   const data = await apiFetch(`${BASE_URL}/notifications/me`);

    state.notifications = Array.isArray(data)
      ? data
      : (data.notifications || data.data || []);

    state.notifCount = state.notifications.filter(n => !n.read).length;

  } catch (err) {
    console.error('Notifications fetch error:', err);
    state.notificationsError = err.message || 'Failed to load notifications';
    state.notifications = [];
    state.notifCount = 0;
  } finally {
    state.notificationsLoading = false;
    patchContent();
    updateTopbar();
  }
}
function updateTopbar(){
  const el = document.getElementById('cp-topbar');
  if(el){
    el.innerHTML = renderTopbar();
  }
}
function deleteNotification(id) {
  state.deleteConfirmNotifId = id;
  patchContent();
}

async function confirmDeleteNotification() {
  const id = state.deleteConfirmNotifId;
  if (!id) return;
  
  state.deleteConfirmNotifId = null;
  patchContent();

  try {
    await apiFetch(`${BASE_URL}/notifications/me/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
    state.notifications = state.notifications.filter(n => n.id !== id);
    state.notifCount = state.notifications.filter(n => !n.read).length;
    patchContent();
  } catch (err) {
    showToast('Failed to delete notification');
  }
}

function cancelDeleteNotification() {
  state.deleteConfirmNotifId = null;
  patchContent();
}
async function markAndDisable(btn, id) {
  btn.disabled = true;
  btn.style.opacity = '0.4';
  btn.textContent = '...';
  await markNotificationRead(id);
  // Update state
  const notif = state.notifications.find(n => n.id === id);
  if (notif) {
    notif.read = true;
    state.notifCount = state.notifications.filter(n => !n.read).length;
  }
  patchContent();
}

async function markAllNotificationsRead() {
  await apiFetch(`${BASE_URL}/notifications/me/mark-all-read`, {
    method: 'PATCH'
  });
}

async function deleteAllNotifications() {
  await apiFetch(`${BASE_URL}/notifications/me/all`, {
    method: 'DELETE'
  });
}

async function handleMarkAllRead() {
  if (!state.notifications?.length) return;

  const prev = [...state.notifications];

  // optimistic UI
  state.notifications = state.notifications.map(n => ({ ...n, read: true }));
  state.notifCount = 0;
  patchContent();
  updateTopbar();

  try {
    await markAllNotificationsRead();
    showToast('All notifications marked as read');
  } catch (e) {
    state.notifications = prev;
    state.notifCount = state.notifications.filter(n => !n.read).length;
    showToast('Failed to mark all as read');
    patchContent();
    updateTopbar();
  }
}
async function handleDeleteAll() {
  if (!state.notifications?.length) return;

  const prev = [...state.notifications];

  state.notifications = [];
  state.notifCount = 0;
  patchContent();
   updateTopbar(); 

  try {
    await deleteAllNotifications();
    showToast('All notifications deleted');
  } catch (e) {
    state.notifications = prev;
     state.notifCount = state.notifications.filter(n => !n.read).length;
    showToast('Failed to delete all');
    patchContent();
    updateTopbar();
  }
}
/* ─────────────────────────────────────────────
   BULK ACTION MODAL HANDLERS
───────────────────────────────────────────── */

function showBulkActionModal(type) {
  state.bulkActionConfirmType = type;
  patchContent();
}

function closeBulkActionModal() {
  state.bulkActionConfirmType = null;
  patchContent();
}

async function confirmBulkAction() {
  const type = state.bulkActionConfirmType;
  if (!type) return;
  
  // Hide modal first
  state.bulkActionConfirmType = null;
  patchContent();
  
  try {
    if (type === 'markAll') {
      await handleMarkAllRead();
      showToast('All notifications marked as read');
    } else if (type === 'deleteAll') {
      await handleDeleteAll();
      showToast('All notifications deleted');
    }
  } catch (e) {
    showToast(type === 'deleteAll' ? 'Failed to delete notifications' : 'Failed to mark as read');
  }
}


    /* ─────────────────────────────────────────────
       LOAD ORDERS  (GET /orders)
    ───────────────────────────────────────────── */
async function loadOrders() {
  state.ordersLoading = true;
  state.ordersError = null;
  patchContent();

  try {
    const data = await apiFetch(API.orders);

    const list = Array.isArray(data)
      ? data
      : (data.orders || data.data || []);

    state.orders = list.map(o => ({
      id: o.id,
  shopifyOrderId: o.shopifyOrderId || o.id,
      orderNumber: o.orderNumber || o.order_number || o.name,
      status: (o.status || o.fulfillment_status || o.financial_status || '').toLowerCase(),
      date: o.date || o.created_at,
      deliveredAt: o.deliveredAt,
      lineItems: o.lineItems || o.line_items || [],
      shippingAddress: o.shippingAddress || o.shipping_address,
      totalPrice: o.totalPrice || o.total_price,
      subtotalPrice: o.subtotalPrice || o.subtotal_price,
      discount: o.discount || o.total_discounts || 0,
      // ADD THESE LINES:
      deliveries: o.deliveries || o.packages || [],
      packages: o.packages || o.deliveries || [],
    }));

  } catch (err) {
    console.error('Orders fetch error:', err);
    state.ordersError = err.message || 'Failed to load orders';
    state.orders = [];
  } finally {
    state.ordersLoading = false;
    patchContent();
  }
}

/* ─────────────────────────────────────────────
   RENDER: VEHICLE PARTS RESULTS
───────────────────────────────────────────── */
function renderVehiclePartsResults() {
  if (!state.vehiclePartsDone && !state.vehiclePartsLoading && !state.vehiclePartsError) return '';

  if (state.vehiclePartsLoading) {
    return `
      <div style="margin-top:24px;text-align:center;padding:40px">
        <div class="cp-spinner" style="width:32px;height:32px;margin:0 auto 12px"></div>
        <p style="color:#888;font-size:14px">Finding compatible parts…</p>
      </div>`;
  }

  if (state.vehiclePartsError) {
    return `
      <div style="margin-top:20px;background:#fff;border:1.5px solid #dde3e8;border-radius:12px;padding:20px;text-align:center">
        <p style="color:#e24b4a;font-weight:600;margin:0 0 8px">Could not load parts</p>
        <p style="color:#888;font-size:13px;margin:0 0 14px">${escHtml(state.vehiclePartsError)}</p>
        <button class="cp-retry-btn" onclick="loadVehicleParts('${escHtml(state.selectedYear)}','${escHtml(state.selectedMake)}','${escHtml(state.selectedModel)}')">Retry</button>
      </div>`;
  }

  if (state.vehicleParts.length === 0) {
    return `
      <div style="margin-top:20px;background:#fff;border:1.5px solid #dde3e8;border-radius:12px;padding:24px;text-align:center">
        <p style="color:#1a2a2a;font-weight:600;margin:0 0 6px">No parts found</p>
        <p style="color:#888;font-size:13px;margin:0">No compatible parts found for ${escHtml(state.selectedYear)} ${escHtml(state.selectedMake)} ${escHtml(state.selectedModel)}</p>
      </div>`;
  }

  return `
    <div style="margin-top:24px">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px">
        <span class="cp-section-title">${state.vehicleParts.length} compatible part${state.vehicleParts.length !== 1 ? 's' : ''}</span>
        <span style="font-size:12px;color:#888">${escHtml(state.selectedYear)} ${escHtml(state.selectedMake)} ${escHtml(state.selectedModel)}</span>
      </div>
      ${state.vehicleParts.map((item, idx) => renderPartCard(item, idx)).join('')}
    </div>`;
}
 function isScannerReady() {
  return typeof Html5Qrcode !== 'undefined';
}

/* ─────────────────────────────────────────────
   RENDER: VIN SCREEN 
───────────────────────────────────────────── */
function renderVin() {
  const scanSupported = !!(navigator.mediaDevices?.getUserMedia);
  const vehicleSelected = state.selectedYear && state.selectedMake && state.selectedModel;

  return `
    ${renderVinScannerModal()}
    ${state.partSummaryModalText ? `
      <div class="cp-modal-overlay" onclick="closePartSummaryModal(event)" style="z-index:2100">
        <div class="cp-modal-content" style="max-width:500px" onclick="event.stopPropagation()">
          <div class="cp-modal-header">
            <span class="cp-modal-title">Summary</span>
            <button class="cp-modal-close" onclick="closePartSummaryModal()">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#888" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div style="max-height:60vh;overflow-y:auto;padding:4px 0">
            <p style="font-size:15px;color:#2D2D2D;line-height:1.6">
              ${escHtml(state.partSummaryModalText)}
            </p>
          </div>
        </div>
      </div>
    ` : ''}

    <div class="cp-screen">

      <!-- ── Top row: Scan + VIN input ── -->
      <div style="display:flex;gap:10px;align-items:center;justify-content:flex-end;width:100%;margin-bottom:4px">

        <button class="cp-btn-primary"
                style="background-color:#FFCC02;color:#1D4064;width:auto;padding:0 14px;height:44px;border-radius:8px;flex-shrink:0;white-space:nowrap;font-size:14px"
                // Inside renderVin(), find the scan button and update onclick:
               onclick="${isScannerReady() ? 'openVinScanner()' : "showToast('Scanner not loaded. Refresh page.')"}">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
            <circle cx="12" cy="13" r="4"/>
          </svg>
          <span style="margin-left:6px">Scan VIN Plate</span>
        </button>

        <input id="cp-vin-input"
               class="cp-input${state.vinError ? ' error' : ''}"
               type="text"
               placeholder="Enter 17-character VIN"
               maxlength="17"
               value="${escHtml(state.vinInput)}"
               oninput="handleVinInput(this.value)"
               onkeydown="if(event.key==='Enter') doVinSearch()"
               style="height:44px;margin:0;flex:2;max-width:300px;font-size:14px;text-transform:uppercase" />

        <button class="cp-search-btn" onclick="doVinSearch()"
                style="height:44px;width:44px;border-radius:8px"
                ${state.vinDecoding ? 'disabled' : ''}>
          ${state.vinDecoding
            ? '<div class="cp-spinner" style="width:18px;height:18px;border-width:2px"></div>'
            : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>'}
        </button>
      </div>

      <!-- Error message -->
      ${state.vinError      ? `<p class="cp-error-text">${escHtml(state.vinError)}</p>` : ''}
      ${state.vinDecodeError ? `<p class="cp-error-text">${escHtml(state.vinDecodeError)}</p>` : ''}
      ${!state.vinError && !state.vinDecodeError ? '<div style="height:8px"></div>' : ''}

      <!-- Decoded vehicle banner -->
      ${state.vinDecodedVehicle ? `
        <div style="
          background:#E0E9F2;
          border:1.5px solid #1D4064;border-radius:12px;
          padding:14px 18px;margin-bottom:16px;
          display:flex;align-items:center;gap:14px;
        ">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <div>
            <p style="font-weight:700;font-size:14px;color:#1D4064;margin:0 0 2px">VIN decoded successfully</p>
            <p style="font-size:13px;color:#2D2D2D;margin:0">
              ${escHtml(state.vinDecodedVehicle.year)} ${escHtml(state.vinDecodedVehicle.make)} ${escHtml(state.vinDecodedVehicle.model)}
            </p>
          </div>
        </div>
      ` : ''}

      <!-- Year / Make / Model accordion -->
      <div class="cp-card">
  <div class="cp-accordion-header" onclick="toggleYearAccordion()">
    <span style="font-weight:600;font-size:15px;color:#1a2a2a">
      ${vehicleSelected
        ? `${escHtml(state.selectedYear)} ${escHtml(state.selectedMake)} ${escHtml(state.selectedModel)}`
        : 'Select Year / Make / Model'}
    </span>
    <div style="display:flex;align-items:center;gap:8px">
      ${vehicleSelected ? `
        <button onclick="event.stopPropagation();clearVinVehicle()"
                style="font-size:12px;color:#e24b4a;background:none;border:1px solid #e24b4a;border-radius:6px;padding:4px 10px;cursor:pointer;font-weight:600">
          Clear
        </button>
      ` : ''}
      ${icons.chevDown(state.yearOpen)}
    </div>
  </div>
  ${state.yearOpen ? renderYearMakeModel() : ''}
</div>

      <!-- Info card -->
      <div class="cp-info-card">
        <div style="margin-top:2px">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4064" stroke-width="2">
            <rect x="1" y="3" width="15" height="13" rx="2"/>
            <path d="M16 8h4l3 5v3h-7V8z"/>
            <circle cx="5.5" cy="18.5" r="2.5"/>
            <circle cx="18.5" cy="18.5" r="2.5"/>
          </svg>
        </div>
        <div>
          <p class="cp-info-title">Find parts by vehicle</p>
          <p class="cp-info-body">
            Scan or enter a VIN to auto-fill year, make and model — or choose them manually from the dropdowns above.
            US VINs are 17 characters (no I, O, Q). Compatible parts appear automatically after selection.
          </p>
        </div>
      </div>

      <!-- Parts results -->
      ${renderVehiclePartsResults()}

    </div>`;
}

//renderVin//HandleVINInput//doVinSearch delete

    function toggleYearAccordion() {
      state.yearOpen = !state.yearOpen;
      if (state.yearOpen && state.vehicleYears.length === 0) {
        loadVehicleYears();
      } else {
        patchContent();
      }
    }

async function cpHandleYearChange(year) {
  state.selectedYear     = year;
  state.selectedMake     = '';
  state.selectedModel    = '';
  state.selectedSubmodel = '';
  state.selectedEngine   = '';
  state.selectedPosition = '';
  state.vehicleMakes     = [];
  state.vehicleModels    = [];
  state.submodelOptions  = [];
  state.engineOptions    = [];
  state.positionOptions  = [];
  state.submodelFetchCompleted = false;
  state.yearOpen = true;
  if (year) await loadMakesForYear(year);
  else patchContent();
}

async function cpHandleMakeChange(make) {
  state.selectedMake     = make;
  state.selectedModel    = '';
  state.selectedSubmodel = '';
  state.selectedEngine   = '';
  state.selectedPosition = '';
  state.vehicleModels    = [];
  state.submodelOptions  = [];
  state.engineOptions    = [];
  state.positionOptions  = [];
  state.submodelFetchCompleted = false;
  if (make) await loadModelsForYearMake(state.selectedYear, make);
  else patchContent();
}

    function cpHandleModelChange(model) {
  state.selectedModel = model;
  if (model && state.selectedYear && state.selectedMake) {
    loadSubmodelsForModel(state.selectedYear, state.selectedMake, model);
  } else {
    patchContent();
  }
}
function cpHandleSubmodelChange(label) {
  state.selectedSubmodel = label;
  loadEnginesForVehicle();
}

function cpHandleEngineChange(label) {
  state.selectedEngine = label;
  loadPositionsForVehicle();
}

    function setSearchTab(tab) {
      state.searchTab = tab;
      patchContent();
    }

  async function doPartSearch() {
  const term = (state.searchInput || '').trim();
  
  if (term.length < 3) {
    state.partSearch.minLengthError = true;
    patchContent();
    showToast('Please enter at least 3 characters to search');
    return;
  }
  
  state.partSearch.minLengthError = false;
  state.partSearch.loading = true;
  state.partSearch.error = null;
  state.partSearch.results = [];
  state.partSearch.done = false;
  patchContent();
  
  try {
    const results = await searchPartsByTerm(term);
    state.partSearch.results = results;
    state.partSearch.done = true;
  } catch (err) {
    state.partSearch.error = err.message || 'Search failed. Please try again.';
    showToast('Search failed. Please try again.');
  } finally {
    state.partSearch.loading = false;
    patchContent();
  }
}
function showDownloadSuccessToast(msg) {
  // Remove existing if any
  var existing = document.getElementById('cp-download-toast');
  if (existing) existing.remove();

  var toast = document.createElement('div');
  toast.id = 'cp-download-toast';
  toast.innerHTML = `
    <div style="display:flex;align-items:center;gap:10px">
      <div style="width:32px;height:32px;background:rgba(255,255,255,0.2);border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7 10 12 15 17 10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
      </div>
      <span style="font-size:14px;font-weight:600">${msg}</span>
    </div>
  `;
  toast.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: #1d9e75;
    color: #fff;
    padding: 14px 20px;
    border-radius: 12px;
    z-index: 9999;
    box-shadow: 0 4px 20px rgba(0,0,0,0.2);
    transform: translateX(120%);
    transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1);
    max-width: 280px;
  `;

  document.body.appendChild(toast);

  // Slide in
  setTimeout(function() {
    toast.style.transform = 'translateX(0)';
  }, 50);

  // Slide out after 3 seconds
  setTimeout(function() {
    toast.style.transform = 'translateX(120%)';
    setTimeout(function() {
      if (toast.parentNode) toast.remove();
    }, 400);
  }, 3000);
}
      /* ─────────────────────────────────────────────
   PDF GENERATION
───────────────────────────────────────────── */
function downloadReceipt(orderId) {
  const element = document.querySelector('.cp-screen'); 
  
  if (!element) {
    showToast("Could not find order details to print");
    return;
  }

  const opt = {
    margin:       [10, 10, 10, 10], 
    filename:     `Order_${orderId}_Receipt.pdf`,
    image:        { type: 'jpeg', quality: 0.98 },
    html2canvas:  { scale: 2 },
    jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
  };

  html2pdf().set(opt).from(element).save().then(function() {
    showDownloadSuccessToast('Receipt downloaded successfully!');
  });
}


/* ─────────────────────────────────────────────
   RMA: ORDER SELECTION HELPERS
───────────────────────────────────────────── */

const RMA_ELIGIBLE_DAYS = 10;

function isOrderEligibleForRma(order) {
  const status = (order.status || '').toLowerCase();
  if (status !== 'delivered') return false;
  
  const deliveryDate = order.deliveredAt || order.date;
  if (!deliveryDate) return false;
  
  const delivered = new Date(deliveryDate);
  delivered.setHours(0, 0, 0, 0); // normalize to start of delivery day
  
  const now = new Date();
  now.setHours(0, 0, 0, 0); // normalize to start of today
  
  const diffDays = Math.round((now - delivered) / (1000 * 60 * 60 * 24));
  
  return diffDays <= RMA_ELIGIBLE_DAYS; // 0 = today, 10 = exactly 10 days later, both eligible
}
function getEligibleOrdersForRma(orders, existingRmas) {
  const excludedOrderIds = new Set(
    (existingRmas || [])
      .filter(r => (r.status || '').toUpperCase() !== 'CANCELLED')
      .map(r => r.orderId || r.order_id || r.id)
  );
  
  return (orders || [])
    .filter(o => !excludedOrderIds.has(o.id))
    .filter(isOrderEligibleForRma)
    .sort((a, b) => new Date(b.deliveredAt || b.date) - new Date(a.deliveredAt || a.date));
}

function formatOrderDateForRma(order) {
  const dateStr = order.deliveredAt || order.date;
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
}

async function openRmaOrderSelector() {
  state.rmaNew.orderSelectorOpen = true;
  patchContent();
  
  if (!state.orders) {
    await loadOrders(); 
  }
  if (!state.rmas) {
    await loadRmas(); 
  }
  
  state.rmaNew.eligibleOrders = getEligibleOrdersForRma(state.orders, state.rmas);
  patchContent();
}

function closeRmaOrderSelector() {
  state.rmaNew.orderSelectorOpen = false;
  patchContent();
}

function selectRmaOrder(order) {
  console.log('RAW LINE ITEMS:', JSON.stringify(order.lineItems?.slice(0,2), null, 2));

  const productItems = (order.lineItems || []).filter(item => {
    const title = (item.title || item.sku || '').toLowerCase();
    const partNumber = (item.partNumber || '').toLowerCase();
    const productType = (item.productType || '').toLowerCase();
    return !title.includes('shipping') && !title.includes('tax') && 
           !title.includes('service fee') && !title.includes('delivery') &&
           !title.includes('handling') &&
           !partNumber.includes('shipping_fee') && !partNumber.includes('sales_tax') &&
           !partNumber.includes('service_fee') &&
           !productType.includes('delivery') && !productType.includes('shipping') &&
           !productType.includes('tax');
  });

  state.rmaNew.selectedOrder = {
    id: order.id,
  shopifyOrderId: order.shopifyOrderId || order.id,
    orderNumber: order.orderNumber || order.name,
    date: formatOrderDateForRma(order),
    isDeliveryDate: !!order.deliveredAt,
    lineItems: productItems.map((item, index) => {
    const uniqueId = 
    item.productId ||
    item.product_id ||
    item.variantId ||
    item.variant_id ||
    item.id ||
    item.partNumber ||     
    item.productType ||     
    item.lineItemId ||
    `item-${index}`;

  return {
    id: item.id || item.variant_id || uniqueId,
    productId: String(uniqueId),
    title: item.title || item.name || item.sku || `Item ${index + 1}`,
    sku: item.sku || item.partNumber || item.part_number || '',
    price: item.price || 0,
    quantity: parseInt(item.quantity) || 1,
    image: item.image?.src || item.image || item.properties?.image || null,
    returnedQty: 0
  };
})
  };

  state.rmaNew.selectedItems = {};
  state.rmaNew.orderSelectorOpen = false;
  patchContent();
}
// Update item selection (qty + reason)
function updateRmaItemSelection(productId, updates) {
  state.rmaNew.selectedItems[productId] = {
    ...(state.rmaNew.selectedItems[productId] || {}),
    ...updates
  };
  patchContent();
}



async function handleSearchMakeChange(make) {
  state.searchSelectedMake = make;
  state.searchSelectedModel = '';
  state.searchSelectedSubmodel = '';
  state.searchSelectedEngine = '';
  state.searchSelectedPosition = '';
  state.searchVehicleModels = [];
  state.searchSubmodelOptions = [];
  state.searchEngineOptions = [];
  state.searchPositionOptions = [];
  state.searchSubmodelFetchCompleted = false;
  state.searchVehicleLoading = true;
  patchContent();
  try {
    const data = await apiFetch(`${BASE_URL}/vehicle/search-options?action=get_models&year=${encodeURIComponent(state.searchSelectedYear)}&make=${encodeURIComponent(make)}`);
    const models = Array.isArray(data) ? data : (data.data || []);
    state.searchVehicleModels = models.map(m => typeof m === 'string' ? m : String(m.options || m.name || '')).filter(Boolean);
  } catch(err) {
    state.searchVehicleModels = [];
  } finally {
    state.searchVehicleLoading = false;
    patchContent();
  }
}

async function handleSearchModelChange(model) {
  state.searchSelectedModel = model;
  state.searchSelectedSubmodel = '';
  state.searchSelectedEngine = '';
  state.searchSelectedPosition = '';
  state.searchSubmodelOptions = [];
  state.searchEngineOptions = [];
  state.searchPositionOptions = [];
  state.searchSubmodelFetchCompleted = false;
  state.searchVehicleLoading = true;
  patchContent();
  try {
    const data = await apiFetch(`${BASE_URL}/vehicle/search-options?action=get_submodels&year=${encodeURIComponent(state.searchSelectedYear)}&make=${encodeURIComponent(state.searchSelectedMake)}&model=${encodeURIComponent(model)}`);
    const list = Array.isArray(data) ? data : (data.data || []);
    state.searchSubmodelOptions = list.map(m => ({
      id: String(m.id || m.value || m.options || ''),
      label: String(m.options || m.id || m.name || '')
    })).filter(o => o.label);
  } catch(err) {
    state.searchSubmodelOptions = [];
  } finally {
    state.searchSubmodelFetchCompleted = true;
    state.searchVehicleLoading = false;
    if (state.searchSubmodelOptions.length === 0) {
      await loadSearchEngines();
    } else {
      patchContent();
    }
  }
}

async function handleSearchSubmodelChange(label) {
  state.searchSelectedSubmodel = label;
  await loadSearchEngines();
}

async function handleSearchEngineChange(label) {
  state.searchSelectedEngine = label;
  await loadSearchPositions();
}

async function handleSearchPositionChange(label) {
  state.searchSelectedPosition = label;
  await loadSearchVehicleParts();
}

async function loadSearchEngines() {
  state.searchEngineOptions = [];
  state.searchPositionOptions = [];
  state.searchSelectedEngine = '';
  state.searchSelectedPosition = '';
  state.searchVehicleLoading = true;
  patchContent();
  try {
    const params = new URLSearchParams({
      action: 'get_engines',
      year: state.searchSelectedYear,
      make: state.searchSelectedMake,
      model: state.searchSelectedModel,
    });
    if (state.searchSubmodelOptions.length > 0 && state.searchSelectedSubmodel) {
      const submodelId = state.searchSubmodelOptions.find(o => o.label === state.searchSelectedSubmodel)?.id || state.searchSelectedSubmodel;
      params.set('submodel', submodelId);
    }
    const data = await apiFetch(`${BASE_URL}/vehicle/search-options?${params}`);
    const list = Array.isArray(data) ? data : (data.data || []);
    state.searchEngineOptions = list.map(m => ({
      id: String(m.id || m.value || m.options || ''),
      label: String(m.options || m.id || m.name || '')
    })).filter(o => o.label);
  } catch(err) {
    state.searchEngineOptions = [];
  } finally {
    state.searchVehicleLoading = false;
    if (state.searchEngineOptions.length === 0) {
      await loadSearchPositions();
    } else {
      patchContent();
    }
  }
}

async function loadSearchPositions() {
  state.searchPositionOptions = [];
  state.searchSelectedPosition = '';
  state.searchVehicleLoading = true;
  patchContent();
  try {
    const params = new URLSearchParams({
      action: 'get_positions',
      year: state.searchSelectedYear,
      make: state.searchSelectedMake,
      model: state.searchSelectedModel,
    });
    if (state.searchSubmodelOptions.length > 0 && state.searchSelectedSubmodel) {
      const submodelId = state.searchSubmodelOptions.find(o => o.label === state.searchSelectedSubmodel)?.id || state.searchSelectedSubmodel;
      params.set('submodel', submodelId);
    }
    if (state.searchEngineOptions.length > 0 && state.searchSelectedEngine) {
      const engineId = state.searchEngineOptions.find(o => o.label === state.searchSelectedEngine)?.id || state.searchSelectedEngine;
      params.set('engine', engineId);
    }
    const data = await apiFetch(`${BASE_URL}/vehicle/search-options?${params}`);
    const list = Array.isArray(data) ? data : (data.data || []);
    state.searchPositionOptions = list.map(m => ({
      id: String(m.id || m.value || m.options || ''),
      label: String(m.options || m.id || m.name || '')
    })).filter(o => o.label);

    if (state.searchPositionOptions.length > 0) {
      state.searchSelectedPosition = state.searchPositionOptions[0].label;
    } else {
      state.searchSelectedPosition = 'N/A';
    }
  } catch(err) {
    state.searchPositionOptions = [];
    state.searchSelectedPosition = 'N/A';
  } finally {
    state.searchVehicleLoading = false;
   await loadSearchVehicleParts();
  }
}

async function loadSearchVehicleParts() {
  state.searchVehiclePartsLoading = true;
  state.searchVehiclePartsError = null;
  state.searchVehicleParts = [];
  state.searchVehiclePartsDone = false;
  patchContent();

  try {
    const params = new URLSearchParams();
    params.set('action', 'get_parts');
    params.set('year', state.searchSelectedYear);
    params.set('make', state.searchSelectedMake);
    params.set('model', state.searchSelectedModel);

    if (state.searchSubmodelOptions.length > 0 && state.searchSelectedSubmodel) {
      const submodelId = state.searchSubmodelOptions.find(o => o.label === state.searchSelectedSubmodel)?.id || state.searchSelectedSubmodel;
      params.set('submodel', submodelId);
    }
    if (state.searchEngineOptions.length > 0 && state.searchSelectedEngine) {
      const engineId = state.searchEngineOptions.find(o => o.label === state.searchSelectedEngine)?.id || state.searchSelectedEngine;
      params.set('engine', engineId);
    }

    const positionId = state.searchPositionOptions.length === 0
      ? '1'
      : (!state.searchSelectedPosition || state.searchSelectedPosition === 'N/A')
        ? '1'
        : (state.searchPositionOptions.find(o => o.label === state.searchSelectedPosition)?.id || state.searchSelectedPosition);
    params.set('position', positionId);

    const token = localStorage.getItem('token');
    const res = await fetch(`${BASE_URL}/vehicle/search-options-warehouse?${params}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    state.searchVehicleParts = Array.isArray(data) ? data : (data.data || data.products || data.results || []);
    state.searchVehiclePartsDone = true;

  } catch (err) {
    state.searchVehiclePartsError = err.message || 'Could not load parts.';
  } finally {
    state.searchVehiclePartsLoading = false;
    patchContent();
  }
}

function renderSearchVehiclePartsResults() {
  if (!state.searchVehiclePartsDone && !state.searchVehiclePartsLoading && !state.searchVehiclePartsError) return '';

  if (state.searchVehiclePartsLoading) {
    return `
      <div style="margin-top:24px;text-align:center;padding:40px">
        <div class="cp-spinner" style="width:32px;height:32px;margin:0 auto 12px"></div>
        <p style="color:#888;font-size:14px">Finding compatible parts…</p>
      </div>`;
  }

  if (state.searchVehiclePartsError) {
    return `
      <div style="margin-top:20px;background:#fff;border:1.5px solid #dde3e8;border-radius:12px;padding:20px;text-align:center">
        <p style="color:#e24b4a;font-weight:600;margin:0 0 8px">Could not load parts</p>
        <p style="color:#888;font-size:13px;margin:0 0 14px">${escHtml(state.searchVehiclePartsError)}</p>
        <button class="cp-retry-btn" onclick="loadSearchVehicleParts()">Retry</button>
      </div>`;
  }

  if (state.searchVehicleParts.length === 0) {
    return `
      <div style="margin-top:20px;background:#fff;border:1.5px solid #dde3e8;border-radius:12px;padding:24px;text-align:center">
        <p style="color:#1a2a2a;font-weight:600;margin:0 0 6px">No parts found</p>
        <p style="color:#888;font-size:13px;margin:0">No compatible parts found for ${escHtml(state.searchSelectedYear)} ${escHtml(state.searchSelectedMake)} ${escHtml(state.searchSelectedModel)}</p>
      </div>`;
  }

  return `
    <div style="margin-top:24px">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px">
        <span class="cp-section-title">${state.searchVehicleParts.length} compatible part${state.searchVehicleParts.length !== 1 ? 's' : ''}</span>
        <span style="font-size:12px;color:#888">${escHtml(state.searchSelectedYear)} ${escHtml(state.searchSelectedMake)} ${escHtml(state.searchSelectedModel)}</span>
      </div>
      ${state.searchVehicleParts.map((item, idx) => renderPartCard(item, idx)).join('')}
    </div>`;
}

function initContactForm() {

    console.log("✅ Contact Page Loaded");

    const btn = document.getElementById('cc-submit');

    function showError(id, msg) {
        const err = document.getElementById(`cc-${id}-error`);
        if (err) {
            err.innerText = msg;
            err.style.display = 'block';
        }
    }

    function clearError(id) {
        const err = document.getElementById(`cc-${id}-error`);
        if (err) err.style.display = 'none';
    }

    function validate() {
        let valid = true;

        const name = document.getElementById('cc-name').value.trim();
        const email = document.getElementById('cc-email').value.trim();
        const phone = document.getElementById('cc-phone').value.trim();
        const message = document.getElementById('cc-message').value.trim();

        // Name (alphabets only)
        if (!/^[A-Za-z\s]+$/.test(name) || name.length < 3) {
            showError('name','Name should contain only alphabets');
            valid=false;
        } else clearError('name');

        // Email
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            showError('email','Invalid email');
            valid=false;
        } else clearError('email');

        // Phone (10 digit)
        if (!/^\d{10}$/.test(phone)) {
            showError('phone','Phone must be 10 digits');
            valid=false;
        } else clearError('phone');

        // Message
        if (!message || message.length < 10) {
            showError('message','Minimum 10 characters required');
            valid=false;
        } else clearError('message');

        return valid;
    }

    btn.addEventListener('click', async function () {

        console.log("🔥 Submit clicked");

        if (!validate()) return;

        btn.disabled = true;
        btn.innerText = 'Sending...';

        const payload = {
            firstName: document.getElementById('cc-name').value.trim(),
            lastName: "",
            email: document.getElementById('cc-email').value.trim(),
            phoneNumber: document.getElementById('cc-phone').value.trim(),
            company: "",
            comment: document.getElementById('cc-message').value.trim()
        };

        console.log("📦 Payload:", payload);

        try {
            const res = await fetch('https://api.exhaustexpert.com/contact-form', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            console.log("✅ API STATUS:", res.status);

            const data = await res.json().catch(() => ({}));
            console.log("📨 API RESPONSE:", data);

            if (res.ok) {
                document.getElementById('cc-success').style.display = 'block';

                // clear fields
                document.getElementById('cc-name').value = '';
                document.getElementById('cc-email').value = '';
                document.getElementById('cc-phone').value = '';
                document.getElementById('cc-message').value = '';
            } else {
                alert(data.message || "Submission failed");
            }

        } catch (err) {
            console.error("❌ ERROR:", err);
            alert("Connection error");
        }

        btn.disabled = false;
        btn.innerText = 'Send';
    });
}
function updateHeader() {
    const header = document.getElementById('cp-header');

    if (!header) return;

    if (state.activeTab === 'contact') {
        header.innerHTML = `
          <h2>Contact Us</h2>
          <p style="color:#6c7a89;">We’re here to help! Send us a message.</p>
        `;
    } else if (state.activeTab === 'home') {
        header.innerHTML = `<h2>Dashboard</h2>`;
    }
}
function inputField(id, placeholder) {
    return `
    <div style="margin-top:15px;">
        <input id="cc-${id}" placeholder="${placeholder}" 
        style="
            width:100%;
            padding:12px;
            border:1px solid #dce3ea;
            border-radius:8px;
        ">
        <small id="cc-${id}-error" style="color:#e24b4a;display:none;"></small>
    </div>
    `;
}

function textareaField(id, placeholder) {
    return `
    <div style="margin-top:15px;">
        <textarea id="cc-${id}" placeholder="${placeholder}" 
        style="
            width:100%;
            padding:12px;
            border:1px solid #dce3ea;
            border-radius:8px;
            min-height:100px;
            resize:none;
        "></textarea>
        <small id="cc-${id}-error" style="color:#e24b4a;display:none;"></small>
    </div>
    `;
}